import { __spreadArray, __read, __assign, __rest, __makeTemplateObject, __extends } from 'tslib';
import React__default, { useRef, useLayoutEffect, createElement, cloneElement, forwardRef, Children, isValidElement, useState, useImperativeHandle, createContext, useContext, useEffect, useCallback, useMemo } from 'react';
import { system, compose, color, typography, layout, space, flexbox, grid, border, shadow, position, zIndex, variant, margin } from 'styled-system';
import shouldForwardProp$1, { createShouldForwardProp, props } from '@styled-system/should-forward-prop';
import css from '@styled-system/css';
import { baukastenTheme } from '@hygraph/theme';
export { ThemeProvider as BaukastenProvider } from '@hygraph/theme';
import styled from '@emotion/styled';
import deepmerge from 'deepmerge';
import { themeGet as themeGet$1 } from '@styled-system/theme-get';
import { keyframes } from '@emotion/react';
import _, { get, omitBy, pickBy } from 'lodash';
import { Close, Error as Error$1, Check, CaretUpFill, CaretDownFill, Search, Lock, EyeOff, Eye, Info, Warning, Success, Unpublish, Flash, Publish, ChevronDown, DoubleChevronRight, Menu as Menu$1, DoubleChevronLeft, ChevronUp } from '@hygraph/icons';
import composeRefs from '@seznam/compose-react-refs';
import TextareaAutoSize from 'react-textarea-autosize';
import Downshift from 'downshift';
import { matchSorter } from 'match-sorter';
import TooltipTrigger from 'react-popper-tooltip';
import AnimateHeight from 'react-animate-height';
import { useOverlay, usePreventScroll, useModal, OverlayContainer } from '@react-aria/overlays';
export { OverlayProvider } from '@react-aria/overlays';
import { useDialog } from '@react-aria/dialog';
import ReactFocusLock from 'react-focus-lock';
import { transparentize } from 'polished';
import { ToastContainer as ToastContainer$1, toast } from 'react-toastify';
import { Root, Trigger, Content, Item as Item$2 } from '@radix-ui/react-dropdown-menu';
import { Tabs as Tabs$1, Tab as Tab$1, TabList as TabList$1, TabPanel as TabPanel$1, TabPanels as TabPanels$1 } from '@reach/tabs';
export { TabsKeyboardActivation } from '@reach/tabs';
import { Root as Root$1, Trigger as Trigger$1, Value, Portal, Content as Content$1, ScrollUpButton, Viewport, ScrollDownButton, Item as Item$3, ItemIndicator, ItemText } from '@radix-ui/react-select';
import useResizeObserver from 'use-resize-observer';

var _shouldForwardProp = createShouldForwardProp(__spreadArray(__spreadArray([], __read(props), false), [
    'pointerEvents',
    'visibility',
    'transform',
    'cursor',
    'appearance',
    'textTransform',
    'textDecoration',
    'gap',
    'columnGap',
    'rowGap',
], false));
// Aliases for html width and height
// Refer https://github.com/chakra-ui/chakra-ui/issues/149 for more info
var nativeHTMLPropAlias = ['htmlWidth', 'htmlHeight'];
// Required for react-textarea-autosize
var propsToInclude = [
    'minRows',
    'readOnly',
    'leastDestructiveRef',
    'isOpen',
    'onDismiss',
    'initialFocusRef',
    'dangerouslyBypassFocusLock',
    'forwardedAs',
    'renderElement',
];
function shouldForwardProp(prop) {
    if (__spreadArray(__spreadArray([], __read(propsToInclude), false), __read(nativeHTMLPropAlias), false).includes(prop)) {
        return true;
    }
    else {
        return _shouldForwardProp(prop);
    }
}
// Type assertion necessary due to partial incompatibility of styled-system's and emotion's CSSObject interfaces
var sx = function (props) {
    return css(props.sx)(props.theme || baukastenTheme);
};
var customProps = system({
    cursor: true,
    transform: true,
    visibility: true,
    pointerEvents: true,
    appearance: true,
    textTransform: true,
    textDecoration: true,
    gap: { property: 'gap', scale: 'gap' },
    columnGap: { property: 'columnGap', scale: 'gap' },
    rowGap: { property: 'rowGap', scale: 'gap' },
});
var SystemBox = styled('div', { shouldForwardProp: shouldForwardProp })(sx, {
    boxSizing: 'border-box',
    minWidth: 0,
}, compose(color, typography, layout, space, flexbox, grid, border, shadow, position, zIndex, customProps));
var ForwardingSystemBox = styled('div')(sx, {
    boxSizing: 'border-box',
    minWidth: 0,
}, compose(color, typography, layout, space, flexbox, grid, border, shadow, position, zIndex, customProps));
// Type assertion and constraining T to HTMLDivElement instead of HTMLElement
// allows us to avoid the following TS error:
// 'HTMLDivElement' is assignable to the constraint of type 'T',
// but 'T' could be instantiated with a different subtype of constraint 'HTMLElement'.
// It can, and it will. We adapt T based on context
var Box = React__default.forwardRef(function Box(props, ref) {
    return React__default.createElement(SystemBox, __assign({ ref: ref }, props));
});
var ForwardingBox = React__default.forwardRef(function ForwardingBox(props, ref) {
    return React__default.createElement(ForwardingSystemBox, __assign({ ref: ref }, props));
});

var Text = React__default.forwardRef(function Text(props, ref) {
    return React__default.createElement(Box, __assign({ ref: ref, as: "p" }, props));
});

var Heading = React__default.forwardRef(function Heading(props, ref) {
    var styleAs = props.styleAs, rest = __rest(props, ["styleAs"]);
    var style = typeof styleAs === 'string'
        ? {
            fontSize: styleAs,
            fontWeight: styleAs,
            lineHeight: styleAs,
        }
        : {};
    return (React__default.createElement(Box, __assign({ ref: ref, as: "h2", m: 0 }, style, { fontFamily: "heading" }, rest)));
});

var Divider = React__default.forwardRef(function Divider(_a, ref) {
    var _b = _a.orientation, orientation = _b === void 0 ? 'horizontal' : _b, props = __rest(_a, ["orientation"]);
    var borderSideProps = orientation === 'horizontal'
        ? { borderBottomWidth: '1px', my: 's' }
        : { borderLeftWidth: '1px', mx: 's', height: 'auto' };
    return (React__default.createElement(Box, __assign({ ref: ref, role: "separator", "aria-orientation": orientation, border: "0", borderStyle: "solid", borderColor: "dividerColor", opacity: 0.6 }, borderSideProps, props, { as: "hr" })));
});

var NativeImage = React__default.forwardRef(function NativeImage(_a, ref) {
    var htmlHeight = _a.htmlHeight, htmlWidth = _a.htmlWidth, props = __rest(_a, ["htmlHeight", "htmlWidth"]);
    return React__default.createElement("img", __assign({ ref: ref, width: htmlWidth, height: htmlHeight }, props));
});
var Image = React__default.forwardRef(function Image(props, ref) {
    return React__default.createElement(Box, __assign({ as: NativeImage, ref: ref }, props));
});

var Flex = React__default.forwardRef(function Flex(_a, ref) {
    var columnGap = _a.columnGap, rowGap = _a.rowGap, gap = _a.gap, props = __rest(_a, ["columnGap", "rowGap", "gap"]);
    var getGapProps = function () {
        if (rowGap || columnGap)
            return { columnGap: columnGap, rowGap: rowGap };
        if (gap)
            return { gap: gap };
        return {};
    };
    if ((rowGap || columnGap) && gap) {
        console.warn('When providing `gap` with `rowGap` or `columnGap`, `gap` will not be used.');
    }
    return React__default.createElement(Box, __assign({ ref: ref, display: "flex" }, getGapProps(), props));
});

var Grid = React__default.forwardRef(function Grid(props, ref) {
    return React__default.createElement(Box, __assign({ ref: ref }, props, { display: "grid" }));
});

/** @deprecated Please use `Stack` or `Inline` instead */
var StackOld = React__default.forwardRef(function StackOld(_a, ref) {
    var children = _a.children, _b = _a.space, space = _b === void 0 ? 'm' : _b, _c = _a.isInline, isInline = _c === void 0 ? false : _c, _d = _a.noWrap, noWrap = _d === void 0 ? false : _d, _e = _a.isReversed, isReversed = _e === void 0 ? false : _e, _f = _a.shouldWrapChildren, shouldWrapChildren = _f === void 0 ? false : _f, props = __rest(_a, ["children", "space", "isInline", "noWrap", "isReversed", "shouldWrapChildren"]);
    var flexDirection = 'column';
    if (isInline) {
        flexDirection = 'row';
    }
    if (isReversed) {
        flexDirection = isInline ? 'row-reverse' : 'column-reverse';
    }
    var validChildren = React__default.Children.toArray(children).filter(React__default.isValidElement);
    var negSpace = typeof space === 'object'
        ? Object.keys(space).reduce(function (result, key) {
            var _a;
            return (__assign(__assign({}, result), (_a = {}, _a[key] = "neg".concat(space[key][0].toUpperCase() + space[key].slice(1)), _a)));
        }, {})
        : "neg".concat(space[0].toUpperCase() + space.slice(1));
    return (React__default.createElement(Flex, __assign({ ref: ref, flexDirection: flexDirection, flexWrap: isInline && !noWrap ? 'wrap' : 'inherit', mt: isInline && !noWrap ? negSpace : 0 }, props), React__default.Children.map(validChildren, function (child, index) {
        var isLastChild = React__default.Children.count(validChildren) === index + 1;
        var spacingProps = {};
        if (isInline && !noWrap)
            spacingProps['mt'] = space;
        if (!isLastChild) {
            if (isInline)
                spacingProps[isReversed ? 'ml' : 'mr'] = space;
            else
                spacingProps[isReversed ? 'mt' : 'mb'] = space;
        }
        if (shouldWrapChildren) {
            return (React__default.createElement(Box, __assign({ display: "inline-block" }, spacingProps), child));
        }
        return React__default.cloneElement(child, spacingProps);
    })));
});

function isString(value) {
    return typeof value === 'string';
}
function isNumber(value) {
    return typeof value === 'number';
}
function widthToColumns(width) {
    // can be undefined per ResponsiveValue
    if (!width) {
        return 'auto';
    }
    function repeatAutoFit(width) {
        function px(n) {
            return isString(n) && n.endsWith('px') ? n : n + 'px';
        }
        return "repeat(auto-fit, minmax(".concat(px(width), ", 1fr))");
    }
    if (Array.isArray(width)) {
        return width
            .map(function (value) {
            if (value === null) {
                return repeatAutoFit(0);
            }
            else {
                return repeatAutoFit(value);
            }
        })
            .join();
    }
    else if (isNumber(width) || isString(width)) {
        return repeatAutoFit(width);
    }
    else {
        // can be a generic object per ResponsiveValue
        return 'auto';
    }
}
function countToColumns(columns) {
    function repeat(columns) {
        return "repeat(".concat(isString(columns) || isNumber(columns) ? columns : 1, ", 1fr)");
    }
    if (Array.isArray(columns)) {
        // error without typecasting as any[]:
        // This expression is not callable.
        // Each member of the union type has signatures, but none of those signatures are compatible with each other.
        return columns.map(repeat).join();
    }
    else {
        return repeat(columns);
    }
}
var Tiles = React__default.forwardRef(function Tiles(_a, ref) {
    var width = _a.width, columns = _a.columns, _b = _a.gap, gap = _b === void 0 ? '3px' : _b, props = __rest(_a, ["width", "columns", "gap"]);
    var gridTemplateColumns = !!width
        ? widthToColumns(width)
        : !!columns
            ? countToColumns(columns)
            : 'auto';
    return (React__default.createElement(Box, __assign({ ref: ref, gap: gap, gridTemplateColumns: gridTemplateColumns }, props, { display: "grid" })));
});

var commonStyleFn = compose(color, typography, layout, space);
var pick = function (raw, allowed) {
    return Object.keys(raw)
        .filter(function (key) { return allowed.includes(key); })
        .reduce(function (obj, key) {
        obj[key] = raw[key];
        return obj;
    }, {});
};
var omit = function (raw, notAllowed) {
    return Object.keys(raw)
        .filter(function (key) { return !notAllowed.includes(key); })
        .reduce(function (obj, key) {
        obj[key] = raw[key];
        return obj;
    }, {});
};
var themeGet = function (name, fallback) {
    if (fallback === void 0) { fallback = null; }
    return themeGet$1(name, fallback)({ theme: baukastenTheme });
};
var mergeSx = function (sx, sxFromProps) {
    if (sxFromProps) {
        return deepmerge(sx, sxFromProps);
    }
    return sx;
};
var splitDOMProps = function (props) {
    var DOMProps = {};
    var styledSystemProps = {};
    Object.keys(props).forEach(function (propName) {
        if (shouldForwardProp$1(propName)) {
            DOMProps[propName] = props[propName];
        }
        else {
            styledSystemProps[propName] = props[propName];
        }
    });
    return { DOMProps: DOMProps, styledSystemProps: styledSystemProps };
};

var VisuallyHidden = React__default.forwardRef(function VisuallyHidden(_a, ref) {
    var sx = _a.sx, rest = __rest(_a, ["sx"]);
    return (React__default.createElement(Box, __assign({ ref: ref, position: "absolute !important", height: "1px", width: "1px", margin: "-1px", overflow: "hidden", whiteSpace: "nowrap", sx: mergeSx({
            clip: 'rect(0px 0px 0px 0px)',
        }, sx) }, rest)));
});

var Stack = React__default.forwardRef(function Stack(_a, ref) {
    var children = _a.children, props = __rest(_a, ["children"]);
    return (React__default.createElement(Flex, __assign({ ref: ref, gap: "16", flexDirection: "column" }, props), children));
});

var Inline = React__default.forwardRef(function Inline(_a, ref) {
    var children = _a.children, props = __rest(_a, ["children"]);
    return (React__default.createElement(Flex, __assign({ ref: ref, gap: "16", flexDirection: "row", flexWrap: "wrap" }, props), children));
});

var spin = keyframes(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  0% {\n    transform: rotate(0deg);\n  }\n  100% {\n    transform: rotate(360deg);\n  }\n"], ["\n  0% {\n    transform: rotate(0deg);\n  }\n  100% {\n    transform: rotate(360deg);\n  }\n"])));
var Spinner = React__default.forwardRef(function Spinner(_a, ref) {
    var _b = _a.color, color = _b === void 0 ? 'primary' : _b, _c = _a.emptyColor, emptyColor = _c === void 0 ? 'transparent' : _c, _d = _a.size, size = _d === void 0 ? 'spinner.m' : _d, _e = _a.thickness, thickness = _e === void 0 ? '2px' : _e, _f = _a.speed, speed = _f === void 0 ? '0.45s' : _f, label = _a.label, sx = _a.sx, rest = __rest(_a, ["color", "emptyColor", "size", "thickness", "speed", "label", "sx"]);
    var sizeValue = get(baukastenTheme.sizes, size) || size;
    return (React__default.createElement(Box, __assign({ ref: ref, borderWidth: thickness, borderColor: "currentColor", borderBottomColor: emptyColor, borderLeftColor: emptyColor, borderStyle: "solid", borderRadius: "round", alignSelf: "center", display: "inline-block", color: color, size: sizeValue, sx: mergeSx({
            animation: "".concat(spin, " ").concat(speed, " linear infinite"),
        }, sx), "data-test": "Spinner" }, rest), label && React__default.createElement(VisuallyHidden, null, label)));
});
var templateObject_1;

var iconSizeMap = {
    'avatar.40': 'icon.24',
    'avatar.36': 'icon.24',
    'avatar.32': 'icon.20',
    'avatar.24': 'icon.16',
    'avatar.16': 'icon.12',
};
var Avatar = React__default.forwardRef(function Avatar(_a, ref) {
    var src = _a.src, _b = _a.size, size = _b === void 0 ? 'avatar.32' : _b, placeholder = _a.placeholder, square = _a.square, sx = _a.sx, isLoading = _a.isLoading, icon = _a.icon, rest = __rest(_a, ["src", "size", "placeholder", "square", "sx", "isLoading", "icon"]);
    var borderRadius = square ? 'small' : 'round';
    var sizeValue = get(baukastenTheme.sizes, size) || size;
    var placeholderBg = square
        ? 'avatar.square.background'
        : 'avatar.round.background';
    if (isLoading) {
        return (React__default.createElement(Flex, { size: sizeValue, justifyContent: "center" },
            React__default.createElement(Spinner, { size: "spinner.l" })));
    }
    if (src) {
        return (React__default.createElement(Image, __assign({ ref: ref, borderRadius: borderRadius, size: sizeValue, src: src, minHeight: sizeValue, minWidth: sizeValue, maxWidth: sizeValue, maxHeight: sizeValue, overflow: "hidden", sx: mergeSx({ objectFit: 'cover' }, sx) }, rest)));
    }
    if (icon) {
        var iconIsInactive = typeof icon !== 'function' && icon.inactive;
        var iconColor = iconIsInactive
            ? 'avatar.round.inactive'
            : 'avatar.round.foreground';
        if (!Object.keys(iconSizeMap).includes(size)) {
            throw new Error("".concat(size, " not allowed for Avatar icon"));
        }
        return (React__default.createElement(Flex, __assign({ size: sizeValue, borderRadius: "round", bg: "avatar.round.background", alignItems: "center", justifyContent: "center" }, rest),
            React__default.createElement(Box, { as: typeof icon === 'function' ? icon : icon.renderer, color: iconColor, size: iconSizeMap[size] })));
    }
    if (placeholder) {
        var placeholderColor = square
            ? 'avatar.square.foreground'
            : 'avatar.round.foreground';
        return (React__default.createElement(Box, __assign({ ref: ref, bg: placeholderBg, fontFamily: "heading", fontSize: "calc(0.4 * ".concat(sizeValue, ")"), fontWeight: "medium", size: sizeValue, lineHeight: sizeValue, minHeight: sizeValue, minWidth: sizeValue, textAlign: "center", borderRadius: borderRadius, color: placeholderColor, sx: sx }, rest),
            placeholder.charAt(0).toUpperCase(),
            placeholder.charAt(1)));
    }
    return null;
});

var sizes = {
    small: {
        fontSize: 'tiny',
        lineHeight: 'small',
        height: 'button.small',
        minWidth: 'button.small',
        px: 's',
    },
    medium: {
        height: 'button.medium',
        minWidth: 'button.medium',
        px: 'sm',
    },
    large: {
        height: 'button.large',
        minWidth: 'button.large',
        px: 'ml',
        '&.split-button': {
            px: 'sm',
        },
        '&.split-dropdownbutton': {
            minWidth: '33px',
        },
    },
};
var solid = 'colors.button.solid';
var outline = 'colors.button.outline';
var ghost = 'colors.button.ghost';
var link = 'colors.button.link';
var dashed = 'colors.button.dashed';
var variants = function (variantColor) {
    if (variantColor === void 0) { variantColor = 'primary'; }
    return ({
        solid: {
            color: themeGet("".concat(solid, ".foreground")),
            backgroundColor: themeGet("".concat(solid, ".").concat(variantColor, ".default")),
            '&:hover:not([disabled]), &:focus': {
                backgroundColor: themeGet("".concat(solid, ".").concat(variantColor, ".hover")),
            },
            '&:focus': {
                boxShadow: "0 0 0 2px ".concat(themeGet("".concat(solid, ".").concat(variantColor, ".focus_outline"))),
            },
            '&:disabled': {
                backgroundColor: themeGet("".concat(solid, ".").concat(variantColor, ".disabled")),
            },
        },
        outline: {
            color: themeGet("".concat(outline, ".").concat(variantColor, ".default.foreground")),
            backgroundColor: themeGet("".concat(outline, ".").concat(variantColor, ".default.background")),
            '.baukasten-button-icon': {
                color: themeGet("".concat(outline, ".").concat(variantColor, ".default.icon")),
            },
            '&:hover:not([disabled]), &:focus': {
                backgroundColor: themeGet("".concat(outline, ".").concat(variantColor, ".hover.background")),
                '.baukasten-button-icon': {
                    color: themeGet("".concat(outline, ".").concat(variantColor, ".hover.icon")),
                },
            },
            '&:focus': {
                boxShadow: "0 0 0 2px ".concat(themeGet("".concat(outline, ".").concat(variantColor, ".focus_outline"))),
            },
            '&:disabled': {
                backgroundColor: themeGet("".concat(outline, ".").concat(variantColor, ".disabled.background")),
                color: themeGet("".concat(outline, ".").concat(variantColor, ".disabled.foreground")),
                '.baukasten-button-icon': {
                    color: themeGet("".concat(outline, ".").concat(variantColor, ".disabled.foreground")),
                },
                '&:before': {
                    borderColor: themeGet("".concat(outline, ".").concat(variantColor, ".disabled.border")),
                },
            },
            '&:before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                border: '1px solid',
                borderColor: themeGet("".concat(outline, ".").concat(variantColor, ".default.border")),
                borderRadius: 'small',
                '.inputAddonBefore &': {
                    borderTopRightRadius: '0',
                    borderBottomRightRadius: '0',
                    borderTopLeftRadius: '2',
                    borderBottomLeftRadius: '2',
                    borderRightWidth: '0',
                    borderColor: 'input.initial.border',
                },
                '.inputAddonAfter &': {
                    borderTopLeftRadius: '0',
                    borderBottomLeftRadius: '0',
                    borderTopRightRadius: '2',
                    borderBottomRightRadius: '2',
                    borderLeftWidth: '0',
                    borderColor: 'input.initial.border',
                },
            },
            '&.split-button:before': {
                borderRight: 'none',
                borderTopRightRadius: '0px',
                borderBottomRightRadius: '0px',
            },
            '&.split-button': {
                borderRight: 'solid 1px rgba(0,0,0,0.2)',
            },
            '&.split-dropdownbutton:before': {
                borderLeft: 'none',
                borderTopLeftRadius: '0px',
                borderBottomLeftRadius: '0px',
            },
            '&.selectTrigger': {
                bg: 'select.trigger.background',
                '&:hover:not([disabled]), &:focus': {
                    bg: 'select.trigger.hover',
                },
            },
        },
        ghost: {
            color: themeGet("".concat(ghost, ".").concat(variantColor, ".foreground")),
            '.baukasten-button-icon, .baukasten-button-inner > svg': {
                color: themeGet("".concat(ghost, ".").concat(variantColor, ".icon")),
            },
            '&:hover:not([disabled]), &:focus, &[aria-expanded="true"]': {
                backgroundColor: themeGet("".concat(ghost, ".").concat(variantColor, ".hover.background")),
                '.baukasten-button-icon, .baukasten-button-inner > svg': {
                    color: themeGet("".concat(ghost, ".").concat(variantColor, ".hover.icon")),
                },
            },
            '&:focus': {
                boxShadow: "0 0 0 2px ".concat(themeGet("".concat(ghost, ".").concat(variantColor, ".focus_outline"))),
            },
            '&:disabled': {
                color: themeGet("".concat(ghost, ".").concat(variantColor, ".disabled")),
                '.baukasten-button-icon, .baukasten-button-inner > svg': {
                    color: themeGet("".concat(ghost, ".").concat(variantColor, ".disabled")),
                },
            },
        },
        link: {
            p: 0,
            fontWeight: 400,
            color: themeGet("".concat(link, ".").concat(variantColor, ".default")),
            height: 'auto',
            minWidth: 0,
            fontSize: 'inherit',
            lineHeight: 'inherit',
            verticalAlign: 'baseline',
            '&:hover:not([disabled]), &:focus': {
                color: themeGet("".concat(link, ".").concat(variantColor, ".hover")),
            },
            '& .baukasten-button-inner': {
                position: 'relative',
                '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    height: '1px',
                    width: '100%',
                    backgroundColor: themeGet("".concat(link, ".").concat(variantColor, ".hover")),
                    opacity: 0,
                },
            },
            '&:hover .baukasten-button-inner:after, &:focus .baukasten-button-inner:after': {
                opacity: 1,
            },
            '&:disabled': {
                color: themeGet("".concat(link, ".").concat(variantColor, ".disabled")),
                '& .baukasten-button-inner:after': {
                    opacity: 0,
                },
            },
        },
        unstyled: {
            p: 0,
            fontSize: 'inherit',
            fontWeight: 'inherit',
            height: 'auto',
            minWidth: 0,
            verticalAlign: 'inherit',
            '&:disabled': {
                opacity: 0.5,
            },
        },
        dashed: {
            color: themeGet("".concat(dashed, ".").concat(variantColor, ".default.foreground")),
            backgroundColor: themeGet("".concat(dashed, ".").concat(variantColor, ".default.background")),
            border: '1px dashed',
            borderColor: themeGet("".concat(dashed, ".").concat(variantColor, ".default.border")),
            '.baukasten-button-icon': {
                color: themeGet("".concat(dashed, ".").concat(variantColor, ".default.icon")),
            },
            '&:hover:not([disabled]), &:focus, &:active': {
                backgroundColor: themeGet("".concat(dashed, ".").concat(variantColor, ".hover.background")),
                '.baukasten-button-icon, .baukasten-button-inner > svg': {
                    color: themeGet("".concat(dashed, ".").concat(variantColor, ".hover.icon")),
                },
            },
            '&:focus:not([disabled]), &:active': {
                boxShadow: "0 0 0 2px ".concat(themeGet("".concat(dashed, ".").concat(variantColor, ".focus_outline"))),
            },
            '&:disabled': {
                color: themeGet("".concat(dashed, ".").concat(variantColor, ".disabled.foreground")),
                borderColor: themeGet("".concat(dashed, ".").concat(variantColor, ".disabled.border")),
                '.baukasten-button-icon': {
                    color: themeGet("".concat(dashed, ".").concat(variantColor, ".disabled.foreground")),
                },
            },
        },
    });
};

var sizeVariants = variant({
    prop: 'buttonSize',
    scale: 'buttons.sizes',
    variants: sizes,
});
var ButtonBase = styled(Box)(sizeVariants, function (_a) {
    var variantColor = _a.variantColor;
    return variant({
        scale: 'buttons.variants',
        variants: variants(variantColor),
    });
});
var Button = React__default.forwardRef(function Button(_a, ref) {
    var variant = _a.variant, vc = _a.variantColor, _b = _a.size, size = _b === void 0 ? 'medium' : _b, loading = _a.loading, loadingText = _a.loadingText, disabled = _a.disabled, SpinnerComp = _a.spinnerComp, iconBefore = _a.iconBefore, iconAfter = _a.iconAfter, children = _a.children, sx = _a.sx, as = _a.as, rest = __rest(_a, ["variant", "variantColor", "size", "loading", "loadingText", "disabled", "spinnerComp", "iconBefore", "iconAfter", "children", "sx", "as"]);
    var defaultVariantColor = variant === 'outline' || variant === 'ghost' || variant === 'dashed'
        ? 'secondary'
        : 'primary';
    var variantColor = vc || defaultVariantColor;
    return (React__default.createElement(ButtonBase, __assign({ ref: ref, as: as || 'button', type: !as ? 'button' : undefined, role: !as ? 'button' : undefined, disabled: disabled || loading, appearance: "none", position: "relative", display: "inline-flex", alignItems: "center", textAlign: "center", verticalAlign: "middle", alignSelf: "center", textDecoration: "none", whiteSpace: "nowrap", fontSize: "interface", fontWeight: 500, color: "inherit", bg: "transparent", border: 0, margin: 0, borderRadius: "small", variant: variant || 'solid', variantColor: variantColor, buttonSize: size, lineHeight: "small", sx: mergeSx({
            textDecoration: 'none',
            userSelect: 'none',
            '&:disabled': {
                cursor: 'not-allowed !important',
            },
            '&:hover': {
                cursor: 'pointer',
            },
            '&:focus': {
                outline: 'none',
                zIndex: 1,
            },
            '.inputAddonBefore &': {
                borderTopRightRadius: '0',
                borderBottomRightRadius: '0',
            },
            '.inputAddonAfter &': {
                borderTopLeftRadius: '0',
                borderBottomLeftRadius: '0',
            },
        }, sx) }, rest),
        loading &&
            (SpinnerComp ? (React__default.createElement(SpinnerComp, null)) : (React__default.createElement(Spinner, { mr: loadingText ? 's' : 0, color: "currentColor", size: "spinner.s" }))),
        loading && loadingText,
        !loading && (React__default.createElement(StackOld, { isInline: true, space: "xs", noWrap: true, alignItems: "center", width: "100%", justifyContent: "center" },
            iconBefore && (React__default.createElement(Box, { size: "icon.16", as: iconBefore, ml: "-1px", className: "baukasten-button-icon" })),
            React__default.createElement(Flex, { as: "span", className: "baukasten-button-inner" }, children),
            iconAfter && (React__default.createElement(Box, { size: "icon.16", as: iconAfter, mr: "-1px", className: "baukasten-button-icon" }))))));
});

var IconButton = React__default.forwardRef(function IconButton(_a, ref) {
    var icon = _a.icon, iconSize = _a.iconSize, children = _a.children, variant = _a.variant, rest = __rest(_a, ["icon", "iconSize", "children", "variant"]);
    return (
    // @ts-expect-error
    React__default.createElement(Button, __assign({ ref: ref, p: "0 !important", justifyContent: "center", variant: variant }, rest),
        React__default.createElement(Box, { as: icon, size: iconSize || (variant == 'ghost' ? '18px' : 'icon.16'), focusable: "false", sx: { pointerEvents: 'none' } })));
});

var Pill = React__default.forwardRef(function Pill(_a, ref) {
    var color = _a.color, bg = _a.bg, children = _a.children, backgroundColor = _a.backgroundColor, _b = _a.size, size = _b === void 0 ? '20' : _b, _c = _a.isDeletable, isDeletable = _c === void 0 ? false : _c, onDelete = _a.onDelete, sx = _a.sx, rest = __rest(_a, ["color", "bg", "children", "backgroundColor", "size", "isDeletable", "onDelete", "sx"]);
    var bgColor = bg || backgroundColor;
    return (React__default.createElement(Box, __assign({ display: isDeletable ? 'inline-flex' : 'inline-block', gap: "4", as: "span", ref: ref, lineHeight: "16px", fontSize: "12px", fontWeight: "medium", px: "s", py: size === '20' ? 'xxs' : 'xs', borderRadius: 4, bg: bgColor || 'pill.background', color: color || (bgColor ? 'white' : 'pill.foreground'), sx: mergeSx({
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            verticalAlign: 'middle',
        }, sx) }, rest),
        children,
        isDeletable && (React__default.createElement(IconButton, { size: "small", variant: "unstyled", icon: Close, onClick: onDelete, color: "pill.icon" }))));
});

var Label = React__default.forwardRef(function Label(props, ref) {
    var _a = props.large, large = _a === void 0 ? false : _a, _b = props.disabled, disabled = _b === void 0 ? false : _b, rest = __rest(props, ["large", "disabled"]);
    return (React__default.createElement(Text, __assign({ ref: ref, display: "inline-flex", color: disabled ? 'input.label.disabled' : 'input.label.default', fontSize: large ? 'copy' : 'interface', fontWeight: 500, lineHeight: large ? '20px' : '16px', alignItems: "center", as: "label" }, rest)));
});

var Hint = React__default.forwardRef(function Hint(props, ref) {
    return (React__default.createElement(Text, __assign({ ref: ref, color: "input.hint.default", lineHeight: "small" }, props)));
});

var InputError = React__default.forwardRef(function InputError(props, ref) {
    var children = props.children, withoutIcon = props.withoutIcon, rest = __rest(props, ["children", "withoutIcon"]);
    return (React__default.createElement(Text, __assign({ ref: ref, color: "inputErrorText", fontSize: "tiny" }, rest),
        withoutIcon ? null : (React__default.createElement(Box, { as: Error$1, sx: {
                verticalAlign: 'text-top',
                color: 'red.500',
                marginRight: 'xs',
                width: 'icon.16',
                height: 'icon.16',
            } })),
        children));
});

var InputGroupElementNames;
(function (InputGroupElementNames) {
    InputGroupElementNames["Input"] = "Input";
    InputGroupElementNames["InputInsetBefore"] = "InputInsetBefore";
    InputGroupElementNames["InputInsetAfter"] = "InputInsetAfter";
    InputGroupElementNames["InputAddonBefore"] = "InputAddonBefore";
    InputGroupElementNames["InputAddonAfter"] = "InputAddonAfter";
})(InputGroupElementNames || (InputGroupElementNames = {}));
function extractInputGroupElements(children) {
    var validChildren = getValidChildren(children);
    var elementNames = Object.keys(InputGroupElementNames);
    var elements = validChildren.reduce(function (acc, child) {
        var type = child.type;
        if (type.displayName && elementNames.includes(type.displayName)) {
            acc[type.displayName] = child;
        }
        return acc;
    }, {});
    return elements;
}
function InputGroup(_a) {
    var children = _a.children, size = _a.size, rest = __rest(_a, ["children", "size"]);
    var _b = extractInputGroupElements(children), Input = _b.Input, InputAddonAfter = _b.InputAddonAfter, InputAddonBefore = _b.InputAddonBefore, InputInsetAfter = _b.InputInsetAfter, InputInsetBefore = _b.InputInsetBefore;
    var insetBeforeRef = useRef();
    var insetAfterRef = useRef();
    var inputRef = useRef();
    var inputStyles = __assign(__assign({ size: size, boxShadow: 'none' }, (InputAddonBefore && {
        borderTopLeftRadius: '0',
        borderBottomLeftRadius: '0',
    })), (InputAddonAfter && {
        borderTopRightRadius: '0',
        borderBottomRightRadius: '0',
    }));
    var commonInsetStyles = {
        position: 'absolute',
        top: 0,
        bottom: 0,
        zIndex: 1,
        // Add a class to the icons in the style presets and remove this line
        color: 'input.icon',
    };
    var insetBeforeStyles = __assign(__assign({}, commonInsetStyles), { left: 0, pl: size === '32' ? '8' : '12', pr: '4' });
    var insetAfterStyles = __assign(__assign({}, commonInsetStyles), { right: 0, px: size === '32' ? '8' : '12' });
    useLayoutEffect(function () {
        if (!inputRef.current)
            return;
        if (insetBeforeRef.current) {
            inputRef.current.style.paddingLeft = "".concat(insetBeforeRef.current.getBoundingClientRect().width, "px");
        }
        if (insetAfterRef.current) {
            inputRef.current.style.paddingRight = "".concat(insetAfterRef.current.getBoundingClientRect().width, "px");
        }
    }); // no dependancy array so it gets recalculated when insets change
    return (createElement(Flex, __assign({ width: "100%", boxShadow: size === '32' ? 'none' : '0 2px 4px rgba(0,0,0,0.05)' }, rest),
        InputAddonBefore,
        createElement(Flex, { position: "relative", height: size === '32' ? '32' : '40', width: "100%" },
            InputInsetBefore
                ? cloneElement(InputInsetBefore, __assign(__assign({}, insetBeforeStyles), { 
                    // @ts-expect-error
                    ref: composeRefs(insetBeforeRef, InputInsetBefore.ref) }))
                : null,
            Input
                ? cloneElement(Input, __assign(__assign({}, inputStyles), { 
                    // @ts-expect-error
                    ref: composeRefs(inputRef, Input.ref) }))
                : null,
            InputInsetAfter
                ? cloneElement(InputInsetAfter, __assign(__assign({}, insetAfterStyles), { 
                    // @ts-expect-error
                    ref: composeRefs(insetAfterRef, InputInsetAfter.ref) }))
                : null),
        InputAddonAfter));
}
var InputInsetBefore = forwardRef(function InputInsetBefore(_a, ref) {
    var children = _a.children, rest = __rest(_a, ["children"]);
    return (createElement(Flex, __assign({ alignItems: "center", justifyContent: "center", gap: "8", ref: ref }, rest), children));
});
InputInsetBefore.displayName = InputGroupElementNames.InputInsetBefore;
var InputInsetAfter = forwardRef(function InputInsetAfter(_a, ref) {
    var children = _a.children, rest = __rest(_a, ["children"]);
    return (createElement(Flex, __assign({ alignItems: "center", justifyContent: "center", gap: "8", ref: ref }, rest), children));
});
InputInsetAfter.displayName = InputGroupElementNames.InputInsetAfter;
var addonDefaultStyles = {
    borderRadius: '2',
    alignSelf: 'stretch',
    alignItems: 'center',
    fontWeight: 'medium',
    lineHeight: '16px',
    color: 'input.addon.foreground',
    bg: 'input.addon.background',
    paddingX: 12,
    borderColor: 'input.addon.border',
    borderWidth: '1px',
    borderStyle: 'solid',
};
var InputAddonBefore = function InputAddonBefore(props) {
    var className = props.className, _a = props.variant, variant = _a === void 0 ? 'default' : _a, rest = __rest(props, ["className", "variant"]);
    return (createElement(Flex, __assign({ flexGrow: 0, flexShrink: 0 }, (variant === 'button'
        ? {
            className: className
                ? "".concat(className, " inputAddonBefore")
                : 'inputAddonBefore',
        }
        : {}), (variant === 'default'
        ? __assign(__assign({}, addonDefaultStyles), { borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRightWidth: 0 }) : {}), rest)));
};
InputAddonBefore.displayName = InputGroupElementNames.InputAddonBefore;
var InputAddonAfter = function InputAddonAfter(props) {
    var className = props.className, _a = props.variant, variant = _a === void 0 ? 'default' : _a, rest = __rest(props, ["className", "variant"]);
    return (createElement(Flex, __assign({ flexGrow: 0, flexShrink: 0 }, (variant === 'button'
        ? {
            className: className
                ? "".concat(className, " inputAddonAfter")
                : 'inputAddonAfter',
        }
        : {}), (variant === 'default'
        ? __assign(__assign({}, addonDefaultStyles), { borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeftWidth: 0 }) : {}), rest)));
};
InputAddonAfter.displayName = InputGroupElementNames.InputAddonAfter;
function getValidChildren(children) {
    return Children.toArray(children).filter(function (child) {
        return isValidElement(child);
    });
}

var Input = forwardRef(function Input(_a, ref) {
    var _b = _a.validationStatus, validationStatus = _b === void 0 ? 'initial' : _b, as = _a.as, _c = _a.size, size = _c === void 0 ? '40' : _c, rest = __rest(_a, ["validationStatus", "as", "size"]);
    var _d = splitDOMProps(rest), DOMProps = _d.DOMProps, styledSystemProps = _d.styledSystemProps;
    var getInitialBorderColor = function () {
        if (validationStatus === 'invalid') {
            return 'input.invalid.border';
        }
        if (validationStatus === 'valid') {
            return 'input.valid.border';
        }
        return 'input.initial.border';
    };
    var getFocusBorderColor = function () {
        if (validationStatus === 'invalid') {
            return 'input.invalid.border';
        }
        if (validationStatus === 'valid') {
            return 'input.valid.border';
        }
        return 'input.focus.border';
    };
    return (createElement(Box, __assign({ appearance: "none", ref: ref, as: as || 'input', dir: "auto", width: "100%", border: "1px solid", borderRadius: "input", height: size === '32' ? '32' : '40', px: size === '32' ? '8' : '12', fontFamily: "body", fontSize: size === '32' ? '13px' : '15px', lineHeight: size === '32' ? '16px' : '20px', borderColor: getInitialBorderColor(), boxShadow: size === '32' ? 'none' : '0 2px 4px rgba(0,0,0,0.05)', sx: {
            resize: 'vertical',
            bg: 'input.background',
            transition: 'border-color 0.25s',
            cursor: 'auto',
            '&:disabled': {
                cursor: 'not-allowed',
            },
            '&:focus': {
                outline: 'none',
                borderColor: getFocusBorderColor(),
            },
            '&:-webkit-autofill': {
                boxShadow: 'inputAutofill',
                borderColor: getFocusBorderColor(),
                color: 'input.value',
            },
            '&:read-write': {
                color: 'input.value',
            },
            '&::placeholder': {
                color: 'input.placeholder',
                opacity: 1,
            },
            '&:read-only, &:read-only:not([disabled])': {
                color: 'input.initial.readonly.value',
                bg: 'input.initial.readonly.background',
                '&:focus': {
                    borderColor: 'input.focus.readonly.border',
                },
            },
            '&:disabled, &:disabled:not([read-only])': {
                color: 'input.disabled.value',
                bg: 'input.disabled.background',
            },
        } }, styledSystemProps, DOMProps)));
});
// @ts-expect-error FIX-ME
Input.displayName = InputGroupElementNames.Input;

var TextArea = React__default.forwardRef(function TextArea(_a, ref) {
    var _b = _a.autoSize, autoSize = _b === void 0 ? false : _b, _c = _a.validationStatus, validationStatus = _c === void 0 ? 'initial' : _c, _d = _a.rows, rows = _d === void 0 ? 2 : _d, props = __rest(_a, ["autoSize", "validationStatus", "rows"]);
    var rowProps = { rows: rows };
    if (autoSize) {
        rowProps = { minRows: rows };
    }
    var getInitialBorderColor = function () {
        if (validationStatus === 'invalid') {
            return 'input.invalid.border';
        }
        if (validationStatus === 'valid') {
            return 'input.valid.border';
        }
        return 'input.initial.border';
    };
    var getFocusBorderColor = function () {
        if (validationStatus === 'invalid') {
            return 'input.invalid.border';
        }
        if (validationStatus === 'valid') {
            return 'input.valid.border';
        }
        return 'input.focus.border';
    };
    return (React__default.createElement(Box, __assign({ display: "inline-flex", position: "relative", width: "100%", appearance: "none", border: "1px solid", borderRadius: "input", px: "sm", py: "s", fontFamily: "body", fontSize: "copy", lineHeight: "regular", borderColor: getInitialBorderColor(), bg: "input.background", boxShadow: "input", as: autoSize ? TextareaAutoSize : 'textarea', dir: "auto", ref: ref, "aria-readonly": props.readOnly, sx: {
            resize: 'vertical',
            transition: 'border-color 0.25s',
            '&:read-write': {
                color: 'input.value',
            },
            '&::placeholder': {
                color: 'input.placeholder',
                opacity: 1,
            },
            '&:focus': {
                borderColor: getFocusBorderColor(),
                outline: 'none',
            },
            '&:-webkit-autofill': {
                borderColor: getFocusBorderColor(),
            },
            '&:read-only, &:read-only:not([disabled])': {
                color: 'input.initial.readonly.value',
                bg: 'input.initial.readonly.background',
                '&:focus': {
                    borderColor: 'input.focus.readonly.border',
                },
            },
            '&:disabled, &:disabled:not([read-only])': {
                color: 'input.disabled.value',
                bg: 'input.disabled.background',
            },
        } }, rowProps, props)));
});

function CheckBoxIcon(_a) {
    var checked = _a.checked, disabled = _a.disabled, props = __rest(_a, ["checked", "disabled"]);
    return (React__default.createElement(Box, __assign({}, props, { size: "16", position: "relative", borderRadius: "2", cursor: disabled ? 'not-allowed' : 'pointer', sx: {
            boxSizing: 'content-box',
            border: '1px solid',
            // Unchecked
            bg: disabled
                ? 'checkbox.box.unchecked.disabled.background'
                : 'checkbox.box.unchecked.default.background',
            borderColor: disabled
                ? 'checkbox.box.unchecked.disabled.border'
                : 'checkbox.box.unchecked.default.border',
            '&:hover, input:focus ~ &, input:focus-visible ~ &': {
                borderColor: disabled
                    ? 'checkbox.box.unchecked.disabled.border'
                    : 'checkbox.box.unchecked.hover',
            },
            'input:focus ~ &, input:focus-visible ~ &': {
                outline: '2px solid',
                outlineOffset: 0,
                outlineColor: 'checkbox.box.unchecked.focus_outline',
            },
            // Checked
            'input:checked ~ &': {
                bg: disabled
                    ? 'checkbox.box.checked.disabled'
                    : 'checkbox.box.checked.default',
                borderColor: disabled
                    ? 'checkbox.box.checked.disabled'
                    : 'checkbox.box.checked.default',
                '&:hover, input:focus ~ &, input:focus-visible ~ &': {
                    bg: disabled
                        ? 'checkbox.box.checked.disabled'
                        : 'checkbox.box.checked.hover',
                    borderColor: disabled
                        ? 'checkbox.box.checked.disabled'
                        : 'checkbox.box.checked.hover',
                },
            },
            'input:checked:focus ~ &, input:checked:focus ~ &': {
                outlineColor: 'checkbox.box.checked.focus_outline',
            },
            // Icon
            '.CheckBoxIcon': {
                position: 'absolute',
                top: '-1px',
                left: '-1px',
                transform: 'scale(0)',
                transition: '120ms transform ease-in-out',
                transformOrigin: 'bottom left',
            },
            'input:checked ~ & > .CheckBoxIcon': {
                transform: 'scale(1)',
            },
        } }),
        React__default.createElement(Box, { as: Check, size: "16", className: "CheckBoxIcon", color: "checkbox.box.checked.icon" })));
}
var CheckBox = React__default.forwardRef(function CheckBox(_a, ref) {
    var sx = _a.sx, props = __rest(_a, ["sx"]);
    var _b = splitDOMProps(props), DOMProps = _b.DOMProps, styledSystemProps = _b.styledSystemProps;
    return (React__default.createElement(Box, null,
        React__default.createElement(Flex, __assign({ as: "input", type: "checkbox", appearance: "none", position: "relative", ref: ref }, DOMProps, styledSystemProps)),
        React__default.createElement(Box, { as: CheckBoxIcon, "aria-hidden": "true", checked: props.checked, disabled: props.disabled })));
});

var Radio = React__default.forwardRef(function Radio(_a, ref) {
    var sx = _a.sx, props = __rest(_a, ["sx"]);
    return (React__default.createElement(Box, __assign({ as: "input", type: "radio", appearance: "none", "aria-hidden": "true", ref: ref, cursor: props.disabled ? 'not-allowed' : 'pointer' }, props, { sx: {
            // Outer circle
            display: 'grid',
            placeContent: 'center',
            size: '16',
            borderRadius: 'round',
            border: '1px solid',
            bg: 'checkbox.box.unchecked.default.background',
            borderColor: 'checkbox.box.unchecked.default.border',
            // Un-checked
            '&:hover:not([disabled]), &:focus:not([disabled])': {
                borderColor: 'checkbox.box.unchecked.hover',
            },
            '&:focus:not([disabled])': {
                outline: '2px solid',
                outlineOffset: 0,
                outlineColor: 'checkbox.box.unchecked.focus_outline',
            },
            '&:disabled': {
                bg: 'checkbox.box.unchecked.disabled.background',
                borderColor: 'checkbox.box.unchecked.disabled.border',
            },
            // Checked
            '&:checked': {
                borderColor: 'checkbox.box.checked.default',
                '&::before': {
                    transform: 'scale(1)',
                },
                '&:hover:not([disabled]), &:focus:not([disabled])': {
                    borderColor: 'checkbox.box.checked.hover',
                    '&::before': {
                        bg: 'checkbox.box.checked.hover',
                    },
                },
                '&:focus:not([disabled])': {
                    outlineColor: 'checkbox.box.checked.focus_outline',
                },
                '&:disabled': {
                    borderColor: 'checkbox.box.checked.disabled',
                    '&::before': {
                        bg: 'checkbox.box.checked.disabled',
                    },
                },
            },
            // Inner Circle
            '&::before': {
                content: "''",
                size: '8',
                borderRadius: 'round',
                transform: 'scale(0)',
                transition: '120ms transform ease-in-out',
                bg: 'checkbox.box.checked.default',
            },
        } })));
});

var Switch = React__default.forwardRef(function Switch(_a, ref) {
    var sx = _a.sx, _b = _a.size, size = _b === void 0 ? '20' : _b, props = __rest(_a, ["sx", "size"]);
    var _c = splitDOMProps(props), DOMProps = _c.DOMProps, styledSystemProps = _c.styledSystemProps;
    return (React__default.createElement(Box, __assign({ onMouseDown: function (e) {
            e.preventDefault();
        }, as: "label", display: "inline-block", position: "relative", verticalAlign: "bottom", cursor: props.disabled ? 'not-allowed' : 'pointer', sx: mergeSx({
            input: {
                ':checked ~ .background': {
                    backgroundColor: 'switch.box.checked.default',
                },
                ':disabled ~ .background': {
                    backgroundColor: 'switch.box.unchecked.disabled',
                },
                ':checked:disabled ~ .background': {
                    backgroundColor: 'switch.box.checked.disabled',
                },
            },
            ':hover': {
                '.background': {
                    backgroundColor: 'switch.box.unchecked.hover',
                },
                input: {
                    ':checked ~ .background': {
                        backgroundColor: 'switch.box.checked.hover',
                    },
                    ':disabled ~ .background': {
                        backgroundColor: 'switch.box.unchecked.disabled',
                    },
                    ':checked:disabled ~ .background': {
                        backgroundColor: 'switch.box.checked.disabled',
                    },
                },
            },
            ':focus-within': {
                '.background': {
                    boxShadow: "0px 0px 0px 2px ".concat(themeGet('colors.switch.box.unchecked.focus_outline')),
                },
                input: {
                    ':checked ~ .background': {
                        boxShadow: "0px 0px 0px 2px ".concat(themeGet('colors.switch.box.checked.focus_outline')),
                    },
                },
            },
        }, sx) }, styledSystemProps),
        React__default.createElement(VisuallyHidden, __assign({ as: "input", type: "checkbox", ref: ref }, DOMProps)),
        React__default.createElement(Box, { className: "background", height: size === '16' ? '16' : '20', width: size === '16' ? '32' : '40', borderRadius: "round", backgroundColor: "switch.box.unchecked.default" }),
        React__default.createElement(Box, { as: "span", "aria-hidden": true, position: "absolute", left: 0, size: size === '16' ? '12' : '16', borderRadius: "round", backgroundColor: "switch.box.foreground", transform: "translate(15%, -50%)", top: "50%", sx: {
                transition: 'all 0.1s ease-out',
                'input:checked ~ &': {
                    transform: size === '16' ? 'translate(150%, -50%)' : 'translate(136%, -50%)',
                },
            } })));
});

function isComboboxItemTypeArray(items) {
    return typeof items[0] !== 'string' && items[0].value !== undefined;
}
function isStringArray(items) {
    return typeof items[0] === 'string';
}
var ComboboxWrapper = styled(Box)(templateObject_1$1 || (templateObject_1$1 = __makeTemplateObject(["\n  position: relative;\n  flex-direction: row;\n  flex: 1;\n"], ["\n  position: relative;\n  flex-direction: row;\n  flex: 1;\n"])));
var ComboboxButton = styled(IconButton)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: flex;\n  min-width: 0;\n  border-top-left-radius: 0;\n  border-bottom-left-radius: 0;\n"], ["\n  display: flex;\n  min-width: 0;\n  border-top-left-radius: 0;\n  border-bottom-left-radius: 0;\n"])));
var ComboboxInput = styled(Input)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  border-right-color: transparent;\n  border-bottom-right-radius: 0px;\n  border-top-right-radius: 0px;\n  background: none;\n  border: 0;\n  flex: 1;\n  :focus {\n    border-right-color: transparent;\n    background: #fff;\n    outline: none;\n  }\n  box-shadow: none;\n"], ["\n  border-right-color: transparent;\n  border-bottom-right-radius: 0px;\n  border-top-right-radius: 0px;\n  background: none;\n  border: 0;\n  flex: 1;\n  :focus {\n    border-right-color: transparent;\n    background: #fff;\n    outline: none;\n  }\n  box-shadow: none;\n"])));
// Type assertion necessary due to partial incompatibility of CSSObject interfaces from styled-system and emotion
var Items = styled(StackOld)(function (_a) {
    var dropdownPosition = _a.dropdownPosition;
    return css({
        display: 'block',
        listStyle: 'none',
        p: 0,
        m: '0 !important',
        bg: 'white',
        boxShadow: 'card',
        maxHeight: '256',
        minWidth: '100%',
        overflowY: 'auto',
        position: 'absolute',
        left: 0,
        zIndex: 10,
        mb: 's',
        bottom: dropdownPosition === 'bottom' ? 'auto' : '40px',
    });
});
var Item = styled(Box)(function (props) {
    var itemProps = props.itemProps || {};
    var ComboboxedProps = props.isComboboxed ? props.ComboboxedProps : {};
    var activeProps = props.isActive ? props.activeProps : {};
    return css(__assign(__assign(__assign({ listStyle: 'none', p: 0, m: 0, textIndent: 0, px: 'm', py: 's', cursor: 'pointer', border: 'none', textAlign: 'left', borderTop: 'none', lineHeight: 'none', color: 'ComboboxListText', fontFamily: 'body', fontSize: 'interface', fontWeight: props.isComboboxed ? 'medium' : 'normal', bg: props.isActive ? 'ComboboxBgHover' : 'ComboboxBg' }, itemProps), activeProps), ComboboxedProps));
});
var Combobox = React__default.forwardRef(function Combobox(_a, ref) {
    var items = _a.items, selectedItem = _a.selectedItem, onChange = _a.onChange, onStateChange = _a.onStateChange, onBlur = _a.onBlur, dropdownButtonProps = _a.dropdownButtonProps, searchable = _a.searchable, clearable = _a.clearable, dataCy = _a.dataCy, disabled = _a.disabled, comboboxInputProps = _a.comboboxInputProps, downshiftProps = _a.downshiftProps, className = _a.className, _b = _a.dropdownPosition, dropdownPosition = _b === void 0 ? 'bottom' : _b, hasError = _a.hasError, borderWrapperInputProps = _a.borderWrapperInputProps, openBorderWrapperInputProps = _a.openBorderWrapperInputProps, itemProps = _a.itemProps, itemComboboxedProps = _a.itemComboboxedProps, itemActiveProps = _a.itemActiveProps, sortedAlphabetically = _a.sortedAlphabetically, hideLabel = _a.hideLabel;
    var listItems = (function () {
        if (items.length > 0) {
            if (isComboboxItemTypeArray(items)) {
                return items;
            }
            else if (isStringArray(items)) {
                return items.map(function (item) { return ({ label: item, value: item }); });
            }
            else {
                return [];
            }
        }
        return [];
    })();
    return (React__default.createElement(Downshift, __assign({ selectedItem: listItems.find(function (li) { return li.value === selectedItem; }) || '', onStateChange: onStateChange, onChange: function (item) { return (onChange ? onChange(item && item.value) : undefined); }, itemToString: function (item) { return (item ? item.label : ''); } }, downshiftProps), function (_a) {
        var isOpen = _a.isOpen, getToggleButtonProps = _a.getToggleButtonProps, getItemProps = _a.getItemProps, highlightedIndex = _a.highlightedIndex, dsComboboxedItem = _a.selectedItem, inputValue = _a.inputValue, getInputProps = _a.getInputProps, clearSelection = _a.clearSelection, toggleMenu = _a.toggleMenu, getRootProps = _a.getRootProps, getMenuProps = _a.getMenuProps;
        /* filter Comboboxable items */
        var availableItems = !inputValue || inputValue === dsComboboxedItem.label
            ? sortedAlphabetically
                ? listItems.sort(function (a, b) {
                    // numbers must be coerced into string for comparison
                    return a.label.toString().localeCompare(b.label.toString());
                })
                : listItems
            : matchSorter(listItems, inputValue, {
                keys: ['label', 'value'],
            });
        var inputProps = onBlur
            ? getInputProps({
                onBlur: function (event) {
                    event.preventDefault();
                    onBlur();
                },
            })
            : getInputProps({});
        return (React__default.createElement(ComboboxWrapper, __assign({}, getRootProps(), { className: className }),
            React__default.createElement(Flex, { boxShadow: "input", borderRadius: "input", sx: mergeSx(__assign({ border: '1px solid', borderColor: 'neutral.200', '&:focus-within': {
                        borderColor: hasError ? 'red.500' : 'brand.500',
                        boxShadow: hasError ? 'inputErrorFocus' : 'brand.500',
                        outline: 'none',
                        '[data-icon]': {
                            color: 'neutral.500',
                        },
                    } }, (isOpen
                    ? (openBorderWrapperInputProps === null || openBorderWrapperInputProps === void 0 ? void 0 : openBorderWrapperInputProps.sx) || {}
                    : (borderWrapperInputProps === null || borderWrapperInputProps === void 0 ? void 0 : borderWrapperInputProps.sx) || {}))) },
                React__default.createElement(ComboboxInput, __assign({}, inputProps, comboboxInputProps, { ref: ref, onClick: function () { return toggleMenu(); }, readOnly: searchable === false, disabled: disabled, sx: __assign({ input: {
                            backgroundColor: 'white',
                            border: '0 !important',
                            '&:read-only': {
                                color: 'text',
                            },
                        } }, ((comboboxInputProps === null || comboboxInputProps === void 0 ? void 0 : comboboxInputProps.sx) || {})) })),
                !disabled ? (React__default.createElement(ComboboxButton, __assign({ size: "small", variant: "ghost", border: "none", alignSelf: "stretch", justifyContent: "center", width: "40px" }, getToggleButtonProps({
                    onClick: function () {
                        return selectedItem && clearable ? clearSelection() : null;
                    },
                }), { disabled: disabled }, dropdownButtonProps, { sx: mergeSx({
                        // Added important because it gets rewritten otherwise
                        backgroundColor: 'white !important',
                        p: '0 4px !important',
                        height: 'auto !important',
                        '[data-icon]': {
                            height: 'icon.24',
                            width: 'icon.24',
                            color: isOpen ? 'neutral.700' : 'neutral.500',
                        },
                        '&:focus': {
                            border: 'none !important',
                        },
                        ':disabled': {
                            opacity: 1,
                        },
                    }, (dropdownButtonProps || {}).sx), icon: selectedItem && clearable
                        ? Close
                        : isOpen
                            ? CaretUpFill
                            : CaretDownFill, "data-test": "Combobox_Downshift_Component", "data-cy": dataCy }))) : null),
            isOpen ? (React__default.createElement(Items, __assign({ as: "ul", "data-type": "DropdownItems", dropdownPosition: dropdownPosition }, getMenuProps()), availableItems.map(function (item, index) {
                var Component = item === null || item === void 0 ? void 0 : item.Component;
                var isComboboxed = dsComboboxedItem && dsComboboxedItem.value === item.value;
                var isActive = highlightedIndex === index;
                return (React__default.createElement(Item, __assign({ as: "li", isComboboxed: isComboboxed, isActive: isActive, itemProps: itemProps, ComboboxedProps: itemComboboxedProps, activeProps: itemActiveProps, "data-cy": item.value }, getItemProps({
                    item: item,
                    disabled: item.disabled,
                }), { key: item.value, "data-test": "ComboboxItemEntry", sx: mergeSx(item.disabled ? {
                        opacity: 0.5,
                        cursor: 'default',
                    } : {}) }),
                    React__default.createElement(Flex, { alignItems: "center" },
                        Component ? (React__default.createElement(Component, { isComboboxed: isComboboxed, isActive: isActive, item: item })) : null,
                        !hideLabel && item.label)));
            }))) : null));
    }));
});
Combobox.defaultProps = {
    searchable: true,
    clearable: true,
    sortedAlphabetically: true,
};
var templateObject_1$1, templateObject_2, templateObject_3;

var MultiDownshift = /** @class */ (function (_super) {
    __extends(MultiDownshift, _super);
    function MultiDownshift(props) {
        var _this = _super.call(this, props) || this;
        _this.stateReducer = function (state, changes) {
            var highlightedIndex = state.highlightedIndex;
            switch (changes.type) {
                case Downshift.stateChangeTypes.keyDownEnter:
                case Downshift.stateChangeTypes.clickItem:
                    return __assign(__assign({}, changes), { isOpen: true, highlightedIndex: highlightedIndex ? highlightedIndex - 1 : 0 });
                default:
                    return changes;
            }
        };
        _this.callOnChange = function (downshift) {
            return function () {
                if (_this.props.onChange) {
                    _this.props.onChange(_this.state.selectedItems, _this.getStateAndHelpers(downshift));
                }
            };
        };
        _this.handleSelection = function (selectedItem, downshift) {
            _this.addSelectedItem(selectedItem, _this.callOnChange(downshift));
        };
        _this.getRemoveButtonProps = function (downshift) {
            return function (_a) {
                var item = _a.item, props = __rest(_a, ["item"]);
                return __assign({ onClick: function (e) {
                        e.stopPropagation();
                        _this.removeItem(item, _this.callOnChange(downshift));
                    }, onKeyPress: function (e) {
                        if (e.key === 'Enter') {
                            e.stopPropagation();
                            _this.removeItem(item, _this.callOnChange(downshift));
                        }
                    } }, props);
            };
        };
        _this.state = {
            selectedItems: _this.props.selectedItems || [],
        };
        return _this;
    }
    MultiDownshift.prototype.componentDidUpdate = function (prevProps) {
        if (prevProps.selectedItems &&
            this.props.selectedItems &&
            prevProps.selectedItems.length !== this.props.selectedItems.length) {
            this.setState({ selectedItems: this.props.selectedItems });
        }
    };
    MultiDownshift.prototype.removeItem = function (item, cb) {
        this.setState(function (_a) {
            var selectedItems = _a.selectedItems;
            return {
                selectedItems: selectedItems.filter(function (si) { return si.value !== item.value; }),
            };
        }, cb);
    };
    MultiDownshift.prototype.addSelectedItem = function (item, cb) {
        if (item) {
            this.setState(function (_a) {
                var selectedItems = _a.selectedItems;
                return ({
                    selectedItems: __spreadArray(__spreadArray([], __read(selectedItems), false), [item], false),
                });
            }, cb);
        }
    };
    MultiDownshift.prototype.clearSelectedItems = function (downshift) {
        this.setState({ selectedItems: [] }, this.callOnChange(downshift));
    };
    MultiDownshift.prototype.getStateAndHelpers = function (downshift) {
        var _this = this;
        var selectedItems = this.state.selectedItems;
        return __assign({ getRemoveButtonProps: this.getRemoveButtonProps(downshift), selectedItems: selectedItems, clearSelectedItems: function () { return _this.clearSelectedItems(downshift); } }, downshift);
    };
    MultiDownshift.prototype.render = function () {
        var _this = this;
        var _a = this.props, children = _a.children, props = __rest(_a, ["children"]);
        return (React__default.createElement(Downshift, __assign({}, props, { stateReducer: this.stateReducer, onChange: this.handleSelection, selectedItem: null }), function (downshift) { return children(_this.getStateAndHelpers(downshift)); }));
    };
    return MultiDownshift;
}(React__default.Component));

function isSelectItemTypeArray(items) {
    return typeof items[0] !== 'string' && items[0].value !== undefined;
}
function isStringArray$1(items) {
    return typeof items[0] === 'string';
}
var SelectWrapper = styled(Box)(templateObject_1$2 || (templateObject_1$2 = __makeTemplateObject(["\n  position: relative;\n  flex-direction: row;\n  flex: 1;\n"], ["\n  position: relative;\n  flex-direction: row;\n  flex: 1;\n"])));
var SelectInputWrapper = styled(Box)(templateObject_2$1 || (templateObject_2$1 = __makeTemplateObject(["\n  display: flex;\n"], ["\n  display: flex;\n"])));
var SelectButton = styled(IconButton)(templateObject_3$1 || (templateObject_3$1 = __makeTemplateObject(["\n  display: flex;\n  min-width: 0;\n  border-top-left-radius: 0;\n  border-bottom-left-radius: 0;\n"], ["\n  display: flex;\n  min-width: 0;\n  border-top-left-radius: 0;\n  border-bottom-left-radius: 0;\n"])));
var Items$1 = styled(StackOld)(function () {
    return css({
        display: 'block',
        listStyle: 'none',
        p: 0,
        m: 0,
        bg: 'white',
        boxShadow: 'card',
        maxHeight: '256',
        minWidth: '100%',
        overflowY: 'auto',
        position: 'absolute',
        left: 0,
        zIndex: 10,
        mb: 's',
    });
});
var Item$1 = styled(Box)(function (props) {
    return css({
        listStyle: 'none',
        p: 0,
        m: 0,
        textIndent: 0,
        px: 'm',
        py: 's',
        cursor: 'pointer',
        border: 'none',
        textAlign: 'left',
        borderTop: 'none',
        lineHeight: 'none',
        color: 'selectListText',
        fontFamily: 'body',
        fontSize: 'copy',
        bg: props.isActive ? 'selectBgHover' : 'selectBg',
    });
});
var Values = styled(Box)(function () {
    return css({
        display: 'flex',
        alignItems: 'center',
        p: 'xs',
        width: '100%;',
        minHeight: '40px',
        border: '1px solid',
        borderRadius: 'input',
        borderColor: 'neutral.200',
        borderRightColor: 'transparent',
        borderBottomRightRadius: 0,
        borderTopRightRadius: 0,
        boxShadow: 'input',
    });
});
var MultiSelect = function (_a) {
    var items = _a.items, _b = _a.value, value = _b === void 0 ? [] : _b, onChange = _a.onChange, dropdownButtonProps = _a.dropdownButtonProps, clearable = _a.clearable, dataCy = _a.dataCy, disabled = _a.disabled, prefix = _a.prefix;
    var listItems = (function () {
        if (items.length > 0) {
            var modifiedItems = (function () {
                if (isSelectItemTypeArray(items)) {
                    return items;
                }
                else if (isStringArray$1(items)) {
                    return items.map(function (item) { return ({ label: item, value: item }); });
                }
                else {
                    return [];
                }
            })();
            return modifiedItems;
        }
        return [];
    })();
    return (React__default.createElement(MultiDownshift, { selectedItems: listItems.filter(function (li) { return value.includes(li.value); }), onChange: function (selectedItems, stateHelpers) {
            return onChange
                ? onChange(selectedItems.map(function (si) { return si.value; }), stateHelpers)
                : undefined;
        }, itemToString: function (item) { return (item ? item.label : undefined); } }, function (_a) {
        var openMenu = _a.openMenu, getToggleButtonProps = _a.getToggleButtonProps, clearSelectedItems = _a.clearSelectedItems, toggleMenu = _a.toggleMenu, getRemoveButtonProps = _a.getRemoveButtonProps, isOpen = _a.isOpen, selectedItems = _a.selectedItems, getItemProps = _a.getItemProps, highlightedIndex = _a.highlightedIndex, getRootProps = _a.getRootProps;
        var filteredItems = listItems.filter(function (item) { return !selectedItems.map(function (si) { return si.value; }).includes(item.value); });
        return (React__default.createElement(SelectWrapper, __assign({ "data-test": "SelectWrapper" }, getRootProps()),
            React__default.createElement(SelectInputWrapper, { "data-test": "SelectInputWrapper" },
                React__default.createElement(Values, { onClick: function () { return (!isOpen && !disabled ? openMenu() : null); }, className: "multiselect-values" },
                    !!prefix ? (React__default.createElement(Flex, { alignItems: "center", mr: "s" }, prefix)) : null,
                    React__default.createElement(StackOld, { space: "xs", isInline: true, shouldWrapChildren: true, alignItems: "center" }, selectedItems.map(function (item) { return (React__default.createElement(Pill, __assign({ key: item.value }, (!disabled ? getRemoveButtonProps({ item: item }) : {}), { tabIndex: 0, "data-test": "SelectPill" }),
                        React__default.createElement(Flex, { alignItems: "center" },
                            item.label,
                            !disabled ? (React__default.createElement(Box, { as: Close, sx: {
                                    mr: '-4px',
                                    ml: '4px',
                                    fontSize: '14px',
                                } })) : null))); }))),
                !disabled ? (React__default.createElement(SelectButton, __assign({}, getToggleButtonProps({
                    onClick: function () {
                        if (selectedItems.length && clearable && !disabled) {
                            clearSelectedItems();
                            toggleMenu();
                        }
                    },
                }), { disabled: disabled, size: "small", variant: "ghost", border: "1px solid", borderColor: "neutral.200", borderLeft: "none", boxShadow: "light", alignSelf: "stretch", justifyContent: "center", width: "40px" }, dropdownButtonProps, { sx: mergeSx({
                        p: '0 4px !important',
                        height: 'auto !important',
                        '[data-icon]': {
                            height: 'icon.24',
                            width: 'icon.24',
                        },
                        '&:focus': {
                            border: 'none !important',
                        },
                    }, (dropdownButtonProps || {}).sx), icon: selectedItems.length && clearable
                        ? Close
                        : isOpen
                            ? CaretUpFill
                            : CaretDownFill, "data-test": "Select_Downshift_Component", "data-cy": dataCy }))) : null),
            isOpen && (React__default.createElement(Items$1, { as: "ul" }, filteredItems.map(function (item, index) { return (React__default.createElement(Item$1, __assign({ as: "li", "data-test": "SelectItem", isActive: highlightedIndex === index, "data-cy": filteredItems[index].value, "data-text": filteredItems[index].label }, getItemProps({
                item: item,
            }), { key: item.value }), filteredItems[index].label)); })))));
    }));
};
MultiSelect.defaultProps = {
    clearable: false,
};
var templateObject_1$2, templateObject_2$1, templateObject_3$1;

var FieldSet = function (props) {
    var children = props.children, label = props.label, _a = props.largeLabel, largeLabel = _a === void 0 ? false : _a, rest = __rest(props, ["children", "label", "largeLabel"]);
    return (React__default.createElement(Box, __assign({ as: "fieldset" }, rest),
        React__default.createElement(Label, { as: "legend", large: largeLabel, mb: "sm" }, label),
        children));
};

var INPUT_PROPS_KEYS = [
    'id',
    'onChange',
    'onBlur',
    'onFocus',
    'value',
    'defaultValue',
    'autoComplete',
    'disabled',
    'name',
    'type',
    'readOnly',
    'placeholder',
    'validationStatus',
    'min',
    'max',
    'step',
    'inputMode',
];
var INPUT_PROPS = INPUT_PROPS_KEYS.reduce(function (a, c) {
    var _a;
    return (__assign(__assign({}, a), (_a = {}, _a[c] = c, _a)));
}, {});
function getInputGroupProps(props) {
    return omitBy(props, function (_, key) { return key in INPUT_PROPS || (key === null || key === void 0 ? void 0 : key.startsWith('data-')); });
}
function getInputProps(props) {
    return pickBy(props, function (_, key) { return key in INPUT_PROPS || (key === null || key === void 0 ? void 0 : key.startsWith('data-')); });
}

var SearchInput = forwardRef(function SearchInput(_a, ref) {
    var clearButtonProps = _a.clearButtonProps, rest = __rest(_a, ["clearButtonProps"]);
    var inputGroupProps = getInputGroupProps(rest);
    var _b = getInputProps(rest), value = _b.value, defaultValue = _b.defaultValue, onChange = _b.onChange, inputProps = __rest(_b, ["value", "defaultValue", "onChange"]);
    var _c = __read(useState(value || defaultValue || ''), 2), internalValue = _c[0], setInternalValue = _c[1];
    var inputValue = typeof value !== 'undefined' ? value : internalValue;
    var inputRef = useRef(null);
    useImperativeHandle(ref, function () { return inputRef.current; });
    return (createElement(InputGroup, __assign({}, inputGroupProps),
        createElement(InputInsetBefore, { pointerEvents: "none" },
            createElement(Box, { as: Search, size: "icon.16" })),
        createElement(Input, __assign({}, inputProps, { ref: inputRef, type: "text", onChange: function (e) {
                setInternalValue(e.currentTarget.value);
                onChange === null || onChange === void 0 ? void 0 : onChange(e);
            }, value: inputValue })),
        inputValue ? (createElement(InputInsetAfter, null,
            createElement(IconButton, __assign({}, clearButtonProps, { title: "Clear search", size: "small", icon: Close, variant: "ghost", type: "button", onClick: function (e) {
                    var _a;
                    (_a = clearButtonProps === null || clearButtonProps === void 0 ? void 0 : clearButtonProps.onClick) === null || _a === void 0 ? void 0 : _a.call(clearButtonProps, e);
                    if (inputRef.current) {
                        inputRef.current.focus();
                        // we want to trigger the onChange event on the input
                        // but before we need to set it's internal value just as if the user interacted with it.
                        // For this we need to call the internal value setter of the input, as react is plugged on these
                        // and not input.value = 'something'
                        // @see https://github.com/facebook/react/issues/11488#issuecomment-558874287
                        var valueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
                        valueSetter.call(inputRef.current, '');
                        inputRef.current.dispatchEvent(new Event('input', {
                            bubbles: true,
                            cancelable: true,
                        }));
                    }
                } })))) : null));
});

var PasswordInput = forwardRef(function PasswordInput(_a, ref) {
    var _b = _a.hasIcon, hasIcon = _b === void 0 ? false : _b, toggleButtonProps = _a.toggleButtonProps, InsetAfter = _a.insetAfter, InsetBefore = _a.insetBefore, AddonBefore = _a.addonBefore, addonBeforeVariant = _a.addonBeforeVariant, AddonAfter = _a.addonAfter, addonAfterVariant = _a.addonAfterVariant, rest = __rest(_a, ["hasIcon", "toggleButtonProps", "insetAfter", "insetBefore", "addonBefore", "addonBeforeVariant", "addonAfter", "addonAfterVariant"]);
    var _c = __read(useState(false), 2), isValueShown = _c[0], setIsValueShown = _c[1];
    var inputGroupProps = getInputGroupProps(rest);
    var inputProps = getInputProps(rest);
    var inputRef = useRef(null);
    useImperativeHandle(ref, function () { return inputRef.current; });
    return (createElement(InputGroup, __assign({}, inputGroupProps),
        AddonBefore && (createElement(InputAddonBefore, { variant: addonBeforeVariant },
            createElement(AddonBefore, __assign({ isValueShown: isValueShown, setIsValueShown: setIsValueShown, inputRef: inputRef }, inputProps)))),
        InsetBefore ? (createElement(InputInsetBefore, null,
            createElement(InsetBefore, __assign({ inputRef: inputRef, isValueShown: isValueShown, setIsValueShown: setIsValueShown, existingInset: hasIcon ? (createElement(Box, { as: Lock, size: "icon.16", pointerEvents: "none" })) : undefined }, inputProps)))) : (hasIcon && (createElement(InputInsetBefore, null,
            createElement(Box, { as: Lock, size: "icon.16", pointerEvents: "none" })))),
        createElement(Input, __assign({ ref: inputRef, type: isValueShown ? 'text' : 'password' }, inputProps)),
        InsetAfter ? (createElement(InputInsetAfter, null,
            createElement(InsetAfter, __assign({ inputRef: inputRef, isValueShown: isValueShown, setIsValueShown: setIsValueShown, existingInset: createElement(ToggleButton, { disabled: inputProps.disabled, isValueShown: isValueShown, setIsValueShown: setIsValueShown }) }, inputProps)))) : (createElement(InputInsetAfter, null,
            createElement(ToggleButton, { disabled: inputProps.disabled, isValueShown: isValueShown, setIsValueShown: setIsValueShown }))),
        AddonAfter && (createElement(InputAddonAfter, { variant: addonAfterVariant },
            createElement(AddonAfter, __assign({ isValueShown: isValueShown, setIsValueShown: setIsValueShown, inputRef: inputRef }, inputProps))))));
});
function ToggleButton(props) {
    var isValueShown = props.isValueShown, setIsValueShown = props.setIsValueShown, rest = __rest(props, ["isValueShown", "setIsValueShown"]);
    return (createElement(IconButton, __assign({}, rest, { size: "small", icon: isValueShown ? EyeOff : Eye, variant: "ghost", onClick: function (e) {
            var _a;
            setIsValueShown(!isValueShown);
            (_a = rest === null || rest === void 0 ? void 0 : rest.onClick) === null || _a === void 0 ? void 0 : _a.call(rest, e);
            e.stopPropagation();
        } })));
}

var NumberInput = forwardRef(function NumberInput(_a, ref) {
    var InsetBefore = _a.insetBefore, InsetAfter = _a.insetAfter, AddonBefore = _a.addonBefore, addonBeforeVariant = _a.addonBeforeVariant, AddonAfter = _a.addonAfter, addonAfterVariant = _a.addonAfterVariant, rest = __rest(_a, ["insetBefore", "insetAfter", "addonBefore", "addonBeforeVariant", "addonAfter", "addonAfterVariant"]);
    var inputGroupProps = getInputGroupProps(rest);
    var inputProps = getInputProps(rest);
    var inputRef = useRef(null);
    useImperativeHandle(ref, function () { return inputRef.current; });
    return (createElement(InputGroup, __assign({}, inputGroupProps),
        AddonBefore && (createElement(InputAddonBefore, { variant: addonBeforeVariant },
            createElement(AddonBefore, __assign({ inputRef: inputRef }, inputProps)))),
        InsetBefore && (createElement(InputInsetBefore, null,
            createElement(InsetBefore, __assign({ inputRef: inputRef }, inputProps)))),
        createElement(Input, __assign({ ref: inputRef, type: "number" }, inputProps)),
        InsetAfter && (createElement(InputInsetAfter, null,
            createElement(InsetAfter, __assign({ inputRef: inputRef }, inputProps)))),
        AddonAfter && (createElement(InputAddonAfter, { variant: addonAfterVariant },
            createElement(AddonAfter, __assign({ inputRef: inputRef }, inputProps))))));
});

var Card = React__default.forwardRef(function Card(_a, ref) {
    var _b = _a.noShadow, noShadow = _b === void 0 ? false : _b, rest = __rest(_a, ["noShadow"]);
    return (React__default.createElement(Box, __assign({ ref: ref, bg: "cardBg", borderRadius: "card", boxShadow: noShadow ? 'none' : 'card', border: "card", borderColor: "cardBorder", sx: {
            ':hover': {
                boxShadow: 'cardHover',
            },
        } }, rest)));
});

var CardHeader = React__default.forwardRef(function CardHeader(_a, ref) {
    var _b = _a.variant, variant = _b === void 0 ? 'default' : _b, rest = __rest(_a, ["variant"]);
    return (React__default.createElement(Box, __assign({ ref: ref, px: "l", py: "m", bg: variant === 'tinted' ? 'tintedCardHeaderBg' : 'inherit' }, rest)));
});

var CardContent = React__default.forwardRef(function CardContent(props, ref) {
    return React__default.createElement(Box, __assign({ ref: ref, px: "l", py: "m" }, props));
});

var Tooltip = React__default.forwardRef(function Tooltip(_a, ref) {
    var label = _a.label, ariaLabel = _a.ariaLabel, _b = _a.placement, placement = _b === void 0 ? 'auto' : _b, _c = _a.enterDelay, enterDelay = _c === void 0 ? 0 : _c, _d = _a.exitDelay, exitDelay = _d === void 0 ? 0 : _d, children = _a.children, _e = _a.bg, bg = _e === void 0 ? 'tooltip.background' : _e, wrapperStyle = _a.wrapperStyle, _f = _a.as, as = _f === void 0 ? 'div' : _f, triggerProps = _a.triggerProps, props = __rest(_a, ["label", "ariaLabel", "placement", "enterDelay", "exitDelay", "children", "bg", "wrapperStyle", "as", "triggerProps"]);
    var marginProps = margin.propNames ? pick(props, margin.propNames) : [];
    var rest = margin.propNames ? omit(props, margin.propNames) : [];
    var colorsBg = "colors.".concat(String(bg));
    if (!label && !ariaLabel) {
        return React__default.createElement(React__default.Fragment, null, children);
    }
    return (React__default.createElement(TooltipTrigger, { placement: placement, delayShow: enterDelay, delayHide: exitDelay, tooltip: function (_a) {
            var arrowRef = _a.arrowRef, tooltipRef = _a.tooltipRef, getTooltipProps = _a.getTooltipProps, getArrowProps = _a.getArrowProps, placement = _a.placement;
            return (React__default.createElement(Box, __assign({}, getTooltipProps({
                ref: tooltipRef,
                className: 'tooltip-container',
                data: 'reach-tooltip',
            }), { m: "s", py: "6px", px: "8px", fontSize: "tiny", bg: bg, color: "tooltip.foreground", fontFamily: "body", borderColor: "transparent", maxWidth: "300px", pointerEvents: "none", position: "absolute", boxShadow: "tooltip", whiteSpace: "nowrap", borderRadius: "tooltip", zIndex: themeGet('zIndexes.tooltip'), sx: {
                    transition: 'opacity 0.3s',
                } }, rest),
                React__default.createElement(Box, __assign({}, getArrowProps({
                    className: 'tooltip-arrow',
                    ref: arrowRef,
                    'data-placement': placement,
                }), { sx: {
                        '&.tooltip-arrow': {
                            height: '1rem',
                            width: '1rem',
                            position: 'absolute',
                        },
                        '&.tooltip-arrow::before': {
                            position: 'absolute',
                            zIndex: themeGet('zIndexes.tooltipArrow'),
                            borderStyle: 'solid',
                            content: '""',
                            display: 'block',
                            height: '0px',
                            margin: 'auto',
                            width: '0px',
                        },
                        "&.tooltip-arrow[data-placement='bottom']": {
                            left: 0,
                            top: 0,
                            mt: '-0.4rem',
                        },
                        "&.tooltip-arrow[data-placement='bottom']::before": {
                            borderColor: "transparent transparent ".concat(themeGet(colorsBg, bg), " transparent"),
                            borderWidth: '0 0.5rem 0.4rem 0.5rem',
                        },
                        "&.tooltip-arrow[data-placement='top']": {
                            bottom: 0,
                            left: 0,
                            mb: '-1rem',
                        },
                        "&.tooltip-arrow[data-placement='top']::before": {
                            borderColor: "".concat(themeGet(colorsBg, bg), " transparent transparent transparent"),
                            borderWidth: '0.4rem 0.5rem 0 0.5rem',
                        },
                        "&.tooltip-arrow[data-placement='right']": {
                            left: 0,
                            ml: '-0.4rem',
                        },
                        "&.tooltip-arrow[data-placement='right']::before": {
                            borderColor: "transparent ".concat(themeGet(colorsBg, bg), " transparent transparent"),
                            borderWidth: '0.5rem 0.4rem 0.5rem 0',
                        },
                        "&.tooltip-arrow[data-placement='left']": {
                            mr: '-1rem',
                            right: 0,
                        },
                        "&.tooltip-arrow[data-placement='left']::before": {
                            borderColor: "transparent transparent transparent ".concat(themeGet(colorsBg, bg)),
                            borderWidth: '0.5rem 0 0.5rem 0.4em',
                        },
                    } })),
                React__default.createElement(Box, { ref: ref, lineHeight: "18px" }, label)));
        } }, function (tooltipTriggerProps) {
        var getTriggerProps = tooltipTriggerProps.getTriggerProps, triggerRef = tooltipTriggerProps.triggerRef;
        if (typeof children === 'function')
            return children(tooltipTriggerProps);
        return (React__default.createElement(Box, __assign({ display: "inline-block" }, getTriggerProps({
            'aria-label': ariaLabel,
        }), { as: as }, marginProps, wrapperStyle, triggerProps, { ref: composeRefs(triggerRef, triggerProps === null || triggerProps === void 0 ? void 0 : triggerProps.ref) }), children));
    }));
});

var Collapse = React__default.forwardRef(function Collapse(_a, ref) {
    var _b = _a.isOpen, isOpen = _b === void 0 ? false : _b, animationDuration = _a.animationDuration, _c = _a.animateOpacity, animateOpacity = _c === void 0 ? true : _c, _d = _a.startingHeight, startingHeight = _d === void 0 ? 0 : _d, _e = _a.endingHeight, endingHeight = _e === void 0 ? 'auto' : _e, rest = __rest(_a, ["isOpen", "animationDuration", "animateOpacity", "startingHeight", "endingHeight"]);
    return (React__default.createElement(Box, { sx: {
            '& .rah-animating--to-height-zero': {
                opacity: 0,
                transform: 'translateY(-0.625rem)',
            },
        } },
        React__default.createElement(AnimateHeight, { duration: animationDuration, animateOpacity: animateOpacity, height: isOpen ? endingHeight : startingHeight, applyInlineTransitions: false, style: {
                transition: 'height .2s ease,opacity .2s ease-in-out,transform .2s ease-in-out',
            } },
            React__default.createElement(Box, __assign({ ref: ref }, rest)))));
});

var Alert = React__default.forwardRef(function Alert(_a, ref) {
    var _b = _a.variantColor, variantColor = _b === void 0 ? 'error' : _b, _c = _a.showStatusIcon, showStatusIcon = _c === void 0 ? false : _c, statusIcon = _a.statusIcon, children = _a.children, _d = _a.showCloseIcon, showCloseIcon = _d === void 0 ? false : _d, onClose = _a.onClose, rest = __rest(_a, ["variantColor", "showStatusIcon", "statusIcon", "children", "showCloseIcon", "onClose"]);
    var iconMap = {
        error: Error$1,
        info: Info,
        primary: Info,
        warning: Warning,
        success: Success,
    };
    return (React__default.createElement(Box, __assign({ ref: ref, py: "m", px: "sm", borderRadius: "small", fontFamily: "body", backgroundColor: "feedback.".concat(variantColor, ".background"), color: "feedback.".concat(variantColor, ".foreground") }, rest),
        React__default.createElement(StackOld, { space: "s", isInline: true, noWrap: true, alignItems: "start" },
            showStatusIcon ? (React__default.createElement(Box, { as: statusIcon ? statusIcon : iconMap[variantColor], width: "icon.16", height: "icon.20", color: themeGet("colors.feedback.".concat(variantColor, ".icon")) })) : null,
            React__default.createElement(Box, { fontSize: "interface", lineHeight: "medium", color: themeGet("colors.feedback.".concat(variantColor, ".foreground")), flex: 1 }, children),
            showCloseIcon && (React__default.createElement(IconButton, { maxHeight: "icon.20", alignSelf: "start", variant: "unstyled", icon: Close, iconSize: "icon.16", onClick: onClose })))));
});

function AlertLink(props) {
    var iconAfter = props.iconAfter, children = props.children, rest = __rest(props, ["iconAfter", "children"]);
    return (React__default.createElement(Box, __assign({ display: "inline-flex", alignItems: "center", as: "a", cursor: "pointer", textDecoration: "underline", fontWeight: "medium", color: "currentColor" }, rest),
        children,
        iconAfter && React__default.createElement(Box, { size: "icon.16", as: iconAfter, ml: "xs" })));
}

function Dialog(props) {
    var children = props.children, isOpen = props.isOpen, onDismiss = props.onDismiss, _a = props.preventClosing, preventClosing = _a === void 0 ? false : _a, restoreFocusRef = props.restoreFocusRef, maxWidth = props.maxWidth, _b = props.size, size = _b === void 0 ? 'medium' : _b, _c = props.overlayProps, underlayBoxProps = _c === void 0 ? {} : _c, _d = props.disableScrollLock, disableScrollLock = _d === void 0 ? false : _d, rest = __rest(props, ["children", "isOpen", "onDismiss", "preventClosing", "restoreFocusRef", "maxWidth", "size", "overlayProps", "disableScrollLock"]);
    var ariaLabel = rest["aria-label"], ariaLabelledby = rest["aria-labelledby"];
    if (typeof ariaLabel !== 'string' && typeof ariaLabelledby !== 'string') {
        console.warn('Please provide either ariaLabel or ariaLabelledby props.');
    }
    if (typeof baukastenTheme.sizes.dialog[size] === 'undefined') {
        console.warn("Dialog size ".concat(size, " is not defined in the theme. If you need to set a custom dialog size, please use the maxWidth prop instead."));
    }
    var dialogMaxWidth = maxWidth || themeGet("sizes.dialog.".concat(size));
    var useOverlayProps = {
        isOpen: isOpen,
        onClose: onDismiss,
        isDismissable: !preventClosing,
        isKeyboardDismissDisabled: Boolean(preventClosing),
        shouldCloseOnInteractOutside: function (element) {
            if (preventClosing === true)
                return false;
            // if the event happened outside of the root of the dialog, then it was from a protal form another library and must be ignored.
            var isInDialogUnderlay = element.matches('[data-overlay-container="true"] *');
            if (isInDialogUnderlay)
                return true;
            return false;
        },
    };
    var dialogRef = useRef(null);
    var _e = useOverlay(useOverlayProps, dialogRef), overlayProps = _e.overlayProps, underlayProps = _e.underlayProps;
    // Prevent scrolling while the modal is open, and hide content
    // outside the modal from screen readers.
    usePreventScroll({ isDisabled: disableScrollLock || !isOpen });
    var modalProps = useModal().modalProps;
    // Get props for the dialog and its title
    var dialogProps = useDialog(props, dialogRef).dialogProps;
    return isOpen ? (createElement(OverlayContainer, null,
        createElement(ReactFocusLock, { autoFocus: true, returnFocus: !(restoreFocusRef && restoreFocusRef.current), onDeactivation: function () {
                if (restoreFocusRef && restoreFocusRef.current) {
                    // see https://github.com/theKashey/react-focus-lock#unmounting-and-focus-management
                    window.setTimeout(function () { var _a, _b; return (_b = (_a = restoreFocusRef.current) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a); }, 0);
                }
            } },
            createElement(Flex, __assign({ position: "fixed", zIndex: 2000, top: 0, left: 0, bottom: 0, right: 0, backgroundColor: "dialog.overlay", alignItems: "center", justifyContent: "center", padding: "40px", overflow: "auto" }, underlayBoxProps, underlayProps),
                createElement(Box, __assign({ ref: dialogRef }, overlayProps, dialogProps, modalProps, { bg: "dialog.content.background", color: "dialog.content.foreground", boxShadow: "0px 2px 6px ".concat(themeGet("colors.dialog.content.shadow")), margin: "auto", borderRadius: "4", width: "100%", maxWidth: dialogMaxWidth, overflow: "visible", position: "relative" }, rest), children))))) : null;
}
var DialogHeader = forwardRef(function DialogHeader(props, ref) {
    return (createElement(Box, __assign({ ref: ref, p: "24px", borderBottom: "1px solid", borderBottomColor: "dialog.header_border", borderTopRightRadius: "4", borderTopLeftRadius: "4" }, props)));
});
var DialogContent = forwardRef(function DialogContent(props, ref) {
    return createElement(Box, __assign({ ref: ref, px: "24px", pt: "28px", pb: "40px" }, props));
});
var DialogFooter = forwardRef(function DialogActions(props, ref) {
    var _a = props.isInsideDialogContent, isInsideDialogContent = _a === void 0 ? false : _a, rest = __rest(props, ["isInsideDialogContent"]);
    return (createElement(Inline, __assign({ ref: ref, px: "24px", py: "16px", mx: isInsideDialogContent ? '-24px' : undefined, mb: isInsideDialogContent ? '-40px' : undefined, mt: isInsideDialogContent ? '40px' : undefined, alignItems: "center", justifyContent: "flex-end", borderBottomRightRadius: "4", borderBottomLeftRadius: "4", bg: "dialog.footer" }, rest)));
});

var animation = keyframes(templateObject_1$3 || (templateObject_1$3 = __makeTemplateObject(["\n  0% {\n      background-size: 200% 100%;\n      background-position: left -31.25% top 0%;\n  }\n  50% {\n      background-size: 800% 100%;\n      background-position: left -49% top 0%;\n  }\n  100% {\n      background-size: 400% 100%;\n      background-position: left -102% top 0%;\n  }\n"], ["\n  0% {\n      background-size: 200% 100%;\n      background-position: left -31.25% top 0%;\n  }\n  50% {\n      background-size: 800% 100%;\n      background-position: left -49% top 0%;\n  }\n  100% {\n      background-size: 400% 100%;\n      background-position: left -102% top 0%;\n  }\n"])));
var Progress = React__default.forwardRef(function Progress(_a, ref) {
    var _b = _a.max, max = _b === void 0 ? 100 : _b, roundedCorners = _a.roundedCorners, _c = _a.variant, variant = _c === void 0 ? 'default' : _c, _d = _a.color, color = _d === void 0 ? 'primary' : _d, inactiveColor = _a.inactiveColor, sx = _a.sx, rest = __rest(_a, ["max", "roundedCorners", "variant", "color", "inactiveColor", "sx"]);
    return (React__default.createElement(Box, __assign({ as: "progress", "data-test": "Progress", ref: ref, max: max, appearance: "none", border: "none", width: "100%", height: variant === 'default' ? 'progress.16' : 'progress.5', bg: inactiveColor
            ? inactiveColor
            : transparentize(0.85, themeGet("colors.".concat(color), color)), color: color, borderRadius: !roundedCorners ? '0px' : 'round', verticalAlign: "top", sx: mergeSx({
            '::-webkit-progress-bar': {
                bg: 'transparent',
            },
            '::-webkit-progress-value': {
                bg: 'currentColor',
                borderRadius: !roundedCorners ? '0px' : 'round',
                transition: 'all 0.2s',
            },
            '::-moz-progress-bar': {
                bg: 'currentColor',
                borderRadius: !roundedCorners ? '0px' : 'round',
                transition: 'all 0.2s',
            },
            '::-ms-fill': {
                bg: 'currentColor',
                borderRadius: !roundedCorners ? '0px' : 'round',
                transition: 'all 0.2s',
                border: 'none',
            },
            ':indeterminate': {
                backgroundSize: '200% 100%',
                borderRadius: !roundedCorners ? '0px' : 'round',
                backgroundImage: 'linear-gradient(to right, transparent 50%, currentColor 50%, currentColor 60%, transparent 60%, transparent 71.5%, currentColor 71.5%, currentColor 84%, transparent 84%)',
                animation: "".concat(animation, " 2s infinite linear"),
            },
            ':indeterminate::-moz-progress-bar': {
                bg: 'transparent',
            },
            ':indeterminate::-ms-fill': {
                animationName: 'none',
            },
        }, sx) }, rest)));
});
var templateObject_1$3;

var Menu = React__default.forwardRef(function Menu(_a, ref) {
    var _b = _a.space, space = _b === void 0 ? 'xs' : _b, children = _a.children, rest = __rest(_a, ["space", "children"]);
    return (React__default.createElement(Flex, __assign({ ref: ref, flexDirection: "column", alignItems: "center", color: "menuItemInactiveText", bg: "menuBg", px: "l" }, rest), Children.map(children, function (child, index) {
        if (!isValidElement(child)) {
            return;
        }
        return cloneElement(child, { py: space });
    })));
});

var MenuItem = React__default.forwardRef(function MenuItem(_a, ref) {
    var sx = _a.sx, rest = __rest(_a, ["sx"]);
    return (React__default.createElement(Flex, __assign({ ref: ref, as: "a", py: "xs", position: "relative", height: "100%", width: "100%", cursor: "pointer", justifyContent: "flex-start", alignItems: "center", fontSize: "interface", px: "s", mb: "2px", borderRadius: "small", sx: mergeSx({
            color: 'menuItemInactiveText',
            fontWeight: 'medium',
            outline: 'none',
            opacity: 1,
            '&:hover, &:focus, &:focus-within': {
                bg: 'menuItemInactiveHoverBg',
            },
            '&:hover, &:focus, &:focus-within, &:active, &.active, &[data-selected]': {
                outline: 'none',
            },
            '&:focus, &:focus-within': {
                boxShadow: 'menuItemInactiveFocus',
            },
            '&.active, &[data-selected]': {
                color: 'menuItemActiveText',
                bg: 'menuItemActiveBg',
                '&:focus, &:focus-within': {
                    boxShadow: 'menuItemActiveFocus',
                },
            },
            '&.nested-nav-link': {
                ml: 'm',
                color: 'menuItemInactiveText',
                '&.active, &[data-selected]': {
                    opacity: 1,
                    bg: 'unset',
                    outline: 'none',
                    color: 'menuItemActiveText',
                },
                '&:hover, &:focus, &:focus-within': {
                    bg: 'menuItemActiveBg',
                },
            },
        }, sx) }, rest)));
});

var ToastBox = React__default.forwardRef(function ToastBox(_a, ref) {
    var children = _a.children, variantColor = _a.variantColor, rest = __rest(_a, ["children", "variantColor"]);
    return (React__default.createElement(Box, __assign({ ref: ref, as: Box, borderRadius: "small", border: variantColor !== 'dark' ? 'toast' : 0, py: "m", px: "sm", position: "relative", fontFamily: "body", backgroundColor: "transparent", width: "auto", minWidth: "344px", maxWidth: "344px" }, rest), children));
});
var bounceInRight = keyframes(templateObject_1$4 || (templateObject_1$4 = __makeTemplateObject(["\n  from,\n  60%,\n  75%,\n  90%,\n  to {\n    animation-timing-function: cubic-bezier(0.215, 0.610, 0.355, 1.000);\n  }\n  from {\n    opacity: 0;\n    transform: translate3d(3000px, 0, 0);\n  }\n  60% {\n    opacity: 1;\n    transform: translate3d(-25px, 0, 0);\n  }\n  75% {\n    transform: translate3d(10px, 0, 0);\n  }\n  90% {\n    transform: translate3d(-5px, 0, 0);\n  }\n  to {\n    transform: none;\n  }\n"], ["\n  from,\n  60%,\n  75%,\n  90%,\n  to {\n    animation-timing-function: cubic-bezier(0.215, 0.610, 0.355, 1.000);\n  }\n  from {\n    opacity: 0;\n    transform: translate3d(3000px, 0, 0);\n  }\n  60% {\n    opacity: 1;\n    transform: translate3d(-25px, 0, 0);\n  }\n  75% {\n    transform: translate3d(10px, 0, 0);\n  }\n  90% {\n    transform: translate3d(-5px, 0, 0);\n  }\n  to {\n    transform: none;\n  }\n"])));
var bounceOutRight = keyframes(templateObject_2$2 || (templateObject_2$2 = __makeTemplateObject(["\n  20% {\n    opacity: 1;\n    transform: translate3d(-20px, 0, 0);\n  }\n  to {\n    opacity: 0;\n    transform: translate3d(2000px, 0, 0);\n  }\n"], ["\n  20% {\n    opacity: 1;\n    transform: translate3d(-20px, 0, 0);\n  }\n  to {\n    opacity: 0;\n    transform: translate3d(2000px, 0, 0);\n  }\n"])));
var ToastContainer = function (props) { return (React__default.createElement(Box, { sx: {
        '.Toastify__toast-container': {
            position: 'fixed',
            p: 0,
            width: ['100vw', 'auto'],
            bottom: [0, '1em'],
            m: [0, 'auto'],
            left: [0, 'auto'],
            right: [null, '1em'],
            zIndex: 9999,
        },
        '.Toastify__toast': {
            position: 'relative',
            // leave space between screen edge on small screens
            m: ['s', 0],
            mb: [null, 's'],
            p: 0,
            backgroundColor: 'transparent',
            boxShadow: 'toast',
            width: 'fit-content',
            minHeight: 'auto',
            maxHeight: '800px',
            boxSizing: 'border-box',
            borderRadius: 'small',
            display: 'flex',
            justifyContent: 'space-between',
            overflow: 'hidden',
            cursor: 'pointer',
            direction: 'ltr',
        },
        '.Toastify__toast-body': {
            m: 0,
            p: 0,
            flex: '1 1 auto',
        },
        '.Toastify__bounce-enter--bottom-right': {
            animationName: bounceInRight,
        },
        '.Toastify__bounce-exit--bottom-right': {
            animationName: bounceOutRight,
        },
    } },
    React__default.createElement(ToastContainer$1, __assign({ position: "bottom-right", hideProgressBar: true, closeButton: false }, props)))); };
var iconMap = {
    success: Success,
    error: Error$1,
    warning: Unpublish,
    info: Info,
    primary: Success,
    dark: Flash,
    publish: Publish,
};
function getVariant(variantColor) {
    if (variantColor === 'publish') {
        return baukastenTheme['colors']['feedback'].success;
    }
    return baukastenTheme['colors']['feedback'][variantColor];
}
var Toast = function (_a) {
    var _b = _a.variantColor, variantColor = _b === void 0 ? 'error' : _b, title = _a.title, isClosable = _a.isClosable, description = _a.description, showStatusIcon = _a.showStatusIcon, statusIcon = _a.statusIcon, actionBtnTitle = _a.actionBtnTitle, onClick = _a.onClick, onClose = _a.onClose, duration = _a.duration;
    useLayoutEffect(function () {
        if (duration) {
            var timeout_1 = setTimeout(function () { return onClose === null || onClose === void 0 ? void 0 : onClose(); }, duration);
            return function () { return clearTimeout(timeout_1); };
        }
    }, [onClose, duration]);
    var color = getVariant(variantColor);
    return (React__default.createElement(ToastBox, { backgroundColor: color.background, borderColor: color.border },
        React__default.createElement(StackOld, { isInline: true, space: "m" },
            showStatusIcon && (React__default.createElement(Box, { as: statusIcon ? statusIcon : iconMap[variantColor], height: "icon.20", width: "icon.16", color: color.icon })),
            React__default.createElement(StackOld, { space: "sm", fontSize: "interface", color: color.foreground, alignItems: "start", flex: "1 1 0" },
                React__default.createElement(StackOld, { space: "s", flex: "1 1 0" },
                    title && (React__default.createElement(Text, { fontWeight: "semiBold", lineHeight: "medium", children: title })),
                    description && (React__default.createElement(Text, { fontWeight: "normal", lineHeight: "medium", children: description }))),
                onClick && (React__default.createElement(Box, { fontWeight: "semiBold" },
                    React__default.createElement(Button, { variant: "unstyled", onClick: onClick, children: actionBtnTitle, sx: {
                            '&:hover': { textDecoration: 'underline' },
                        } })))),
            isClosable && (React__default.createElement(IconButton, { alignSelf: "start", variant: "unstyled", icon: Close, onClick: onClose, color: color.foreground })))));
};
function toaster(_a) {
    var _b = _a.position, position = _b === void 0 ? 'bottom-right' : _b, _c = _a.duration, duration = _c === void 0 ? 5000 : _c, render = _a.render, id = _a.id, title = _a.title, description = _a.description, _d = _a.variantColor, variantColor = _d === void 0 ? 'error' : _d, _e = _a.isClosable, isClosable = _e === void 0 ? true : _e, _f = _a.showStatusIcon, showStatusIcon = _f === void 0 ? true : _f, statusIcon = _a.statusIcon, onClick = _a.onClick, onOpen = _a.onOpen, onClose = _a.onClose, _g = _a.update, update = _g === void 0 ? false : _g, actionBtnTitle = _a.actionBtnTitle;
    if (!id && update) {
        console.error('A Toast Id is needed to update a toast, but none was provided');
        update = false;
    }
    var options = {
        position: position,
        autoClose: duration,
        closeOnClick: onClick ? false : isClosable,
        draggable: isClosable,
        onOpen: onOpen,
        onClose: onClose,
        toastId: id,
    };
    var internalToast = update
        ? function (render, options) {
            toast.update(options.toastId, __assign(__assign({}, options), { render: render }));
            return options.toastId;
        }
        : toast;
    if (render) {
        return internalToast(
        // @ts-expect-error
        function (_a) {
            var closeToast = _a.closeToast;
            return render({ closeToast: closeToast, id: id });
        }, options);
    }
    return internalToast(
    // @ts-expect-error
    function (_a) {
        var closeToast = _a.closeToast;
        return (React__default.createElement(Toast, __assign({}, {
            onClose: closeToast,
            title: title,
            description: description,
            variantColor: variantColor,
            isClosable: isClosable,
            showStatusIcon: showStatusIcon,
            statusIcon: statusIcon,
            actionBtnTitle: actionBtnTitle,
            onClick: onClick,
            duration: duration,
        })));
    }, options);
}
var useToast = function () { return toaster; };
var closeToast = toast.dismiss;
var isToastActive = toast.isActive;
var templateObject_1$4, templateObject_2$2;

function DropdownMenu(_a) {
    var children = _a.children, props = __rest(_a, ["children"]);
    var _b = __read(useState(false), 2), isOpened = _b[0], setIsOpened = _b[1];
    var childrenWithProps = typeof children === 'function'
        ? React__default.Children.only(children({ isOpened: isOpened }))
        : children;
    return (React__default.createElement(Root, __assign({}, props, { onOpenChange: function (open) {
            var _a;
            setIsOpened(open);
            (_a = props === null || props === void 0 ? void 0 : props.onOpenChange) === null || _a === void 0 ? void 0 : _a.call(props, open);
        } }), childrenWithProps));
}
var DropdownMenuTrigger = React__default.forwardRef(function (props, ref) {
    var _a = props.as, Component = _a === void 0 ? Button : _a, disabled = props.disabled, rest = __rest(props, ["as", "disabled"]);
    return (React__default.createElement(Trigger, { asChild: true, disabled: disabled },
        React__default.createElement(Component, __assign({ as: "button" }, rest, { ref: ref, sx: __assign(__assign({}, props.sx), { '&:focus-visible': {
                    outline: 'none'
                } }) }))));
});
var DropdownMenuList = function (_a) {
    var children = _a.children, side = _a.side, align = _a.align, _b = _a.sideOffset, sideOffset = _b === void 0 ? 2 : _b, maxWidth = _a.maxWidth, maxHeight = _a.maxHeight, sx = _a.sx, as = _a.as, onCloseAutoFocus = _a.onCloseAutoFocus, rest = __rest(_a, ["children", "side", "align", "sideOffset", "maxWidth", "maxHeight", "sx", "as", "onCloseAutoFocus"]);
    return (React__default.createElement(Content, { side: side, align: align, sideOffset: sideOffset, onCloseAutoFocus: onCloseAutoFocus },
        React__default.createElement(Flex, { p: "4", bg: "dropdown.list.background", border: "1px solid", borderColor: "dropdown.list.border", borderRadius: "4", boxShadow: "dropdownMenuList", minWidth: "60", maxWidth: maxWidth, maxHeight: maxHeight },
            React__default.createElement(Flex, __assign({ display: "flex", overflow: maxHeight ? 'auto' : 'initial', flexDirection: "column", width: "100%" }, rest), children))));
};
function DropdownMenuItem(_a) {
    var _b = _a.disabled, disabled = _b === void 0 ? false : _b, iconBefore = _a.iconBefore, _c = _a.variantColor, variantColor = _c === void 0 ? 'default' : _c, props = __rest(_a, ["disabled", "iconBefore", "variantColor"]);
    var Component = props.as, rest = __rest(props, ["as"]);
    var As = Component
        ? function (props) { return (React__default.createElement(Item$2, { asChild: true },
            React__default.createElement(Component, __assign({}, props)))); }
        : Item$2;
    return (React__default.createElement(Flex, __assign({ gap: "8", py: "8", px: "12", borderRadius: "2", lineHeight: "small", cursor: "pointer", userSelect: "none", bg: "dropdown.item.background", as: As, "data-valuetext": typeof rest.children === 'string' ? rest.children : '' }, rest, { sx: __assign(__assign(__assign(__assign(__assign({}, props.sx), { 
            // Base styles
            alignItems: 'center', '&:where(:hover)': {
                outline: 'none',
                outlineOffset: '0',
            }, '&:where(:focus:not(:hover)), &:where(:focus-visible)': {
                position: 'relative',
                outline: 'none',
                outlineOffset: '0',
            }, 
            // Disabled styles
            color: variantColor === 'default' && disabled
                ? 'dropdown.item.disabled'
                : "dropdown.item.".concat(variantColor, ".disabled") }), (!disabled && {
            color: 'dropdown.item.foreground',
            '.baukasten-dropdown-icon': {
                color: "dropdown.item.icon",
            },
            '&:focus, &:hover': {
                bg: 'dropdown.item.hover.background',
                '.baukasten-dropdown-icon': {
                    color: "dropdown.item.hover.icon",
                },
            },
            '&:focus:not(:hover)': {
                outline: '2px solid',
                outlineColor: 'dropdown.item.focus_outline',
            },
        })), (variantColor !== 'default' &&
            !disabled && {
            color: "dropdown.item.".concat(variantColor, ".foreground"),
            '.baukasten-dropdown-icon': {
                color: "dropdown.item.".concat(variantColor, ".icon"),
            },
            '&:focus, &:hover': {
                bg: "dropdown.item.".concat(variantColor, ".hover.background"),
                '.baukasten-dropdown-icon': {
                    color: "dropdown.item.".concat(variantColor, ".hover.icon"),
                },
            },
            '&:focus:not(:hover)': {
                outline: '2px solid',
                outlineColor: "dropdown.item.".concat(variantColor, ".focus_outline"),
            },
        })), (iconBefore && { alignItems: 'start' })) }),
        iconBefore && (React__default.createElement(Box, { size: "icon.16", as: iconBefore, className: "baukasten-dropdown-icon" })),
        props.children));
}

var Tabs = React__default.forwardRef(function (props, ref) {
    var _a = props.as, as = _a === void 0 ? 'div' : _a, rest = __rest(props, ["as"]);
    return React__default.createElement(ForwardingBox, __assign({ ref: ref, as: RTabs, forwardedAs: as }, rest));
});
var RTabs = React__default.forwardRef(function (props, r) {
    var forwardedAs = props.forwardedAs, rest = __rest(props, ["forwardedAs"]);
    return React__default.createElement(Tabs$1, __assign({ ref: r, as: forwardedAs }, rest));
});

var Tab = React__default.forwardRef(function (props, ref) {
    var _a = props.as, as = _a === void 0 ? 'button' : _a, _b = props.variant, variant = _b === void 0 ? 'default' : _b, rest = __rest(props, ["as", "variant"]);
    return (React__default.createElement(StyledTab, __assign({ ref: ref, as: RTab, forwardedAs: as, px: "12", py: "10", bg: "transparent", border: "none", cursor: "pointer", fontWeight: "medium", letterSpacing: 1.2, sx: {
            textTransform: 'uppercase',
            lineHeight: 'small',
        }, variant: variant }, rest)));
});
var StyledTab = styled(ForwardingBox)({
    outline: 'none',
}, function () {
    return variant({
        variants: variants$1(),
    });
});
var RTab = React__default.forwardRef(function (props, r) {
    var forwardedAs = props.forwardedAs, rest = __rest(props, ["forwardedAs"]);
    return React__default.createElement(Tab$1, __assign({ ref: r, as: forwardedAs }, rest));
});
var pill = 'tab.pill';
var underline = 'tab.underline';
var variants$1 = function () { return ({
    default: {
        flex: 1,
    },
    underline: {
        position: 'relative',
        borderRadius: "4px 4px 0px 0px",
        py: '8',
        transition: 'background-color 0.02s ease-in-out, color 0.02s ease-in-out',
        '&:not(:disabled)': {
            // Inactive state
            bg: "".concat(underline, ".inactive.default.background"),
            color: "".concat(underline, ".inactive.default.foreground"),
            '&:after': {
                content: "' '",
                position: 'absolute',
                height: '2px',
                width: '100%',
                left: 0,
                bottom: 0,
            },
            '&:focus-visible, &:focus': {
                bg: "".concat(underline, ".active.hover"),
                outline: '2px solid',
                outlineOffset: 0,
                outlineColor: "".concat(pill, ".inactive.focus_outline"),
                '&:after': {
                    bg: "".concat(underline, ".active.default.foreground"),
                },
            },
            '&:hover:not(:focus):not([data-selected])': {
                bg: "".concat(underline, ".inactive.hover"),
                '&:after': {
                    bg: "".concat(underline, ".inactive.default.foreground"),
                },
            },
            // Active state
            '&:active, &[data-selected]': {
                color: "".concat(underline, ".active.default.foreground"),
                '&:after': {
                    bg: "".concat(underline, ".active.default.foreground"),
                },
                '&:hover, &:focus': {
                    bg: "".concat(underline, ".active.hover"),
                },
                '&:focus-visible, &:focus': {
                    outlineColor: "".concat(pill, ".active.focus_outline"),
                },
            },
        },
        '&:disabled': {
            bg: "".concat(underline, ".inactive.disabled.background"),
            color: "".concat(underline, ".inactive.disabled.foreground"),
            cursor: 'default',
        },
    },
    pill: {
        borderRadius: 'small',
        transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
        // Inactive state
        bg: "".concat(pill, ".inactive.default.background"),
        color: "".concat(pill, ".inactive.default.foreground"),
        '&:focus': {
            outline: '2px solid',
            outlineColor: "".concat(pill, ".inactive.focus_outline"),
        },
        '&:not(:disabled)': {
            // Inactive state
            '&:hover:not([data-selected]), &:focus:not([data-selected])': {
                bg: "".concat(pill, ".inactive.hover"),
            },
            // Active state
            '&:active, &[data-selected]': {
                bg: "".concat(pill, ".active.default.background"),
                color: "".concat(pill, ".active.default.foreground"),
                '&:hover, &:focus': {
                    bg: "".concat(pill, ".active.hover"),
                },
                '&:focus': {
                    outlineColor: "".concat(pill, ".active.focus_outline"),
                },
            },
        },
        '&:disabled': {
            bg: "".concat(pill, ".inactive.disabled.background"),
            color: "".concat(pill, ".inactive.disabled.foreground"),
            cursor: 'default',
        },
    },
}); };

var TabList = React__default.forwardRef(function (props, ref) {
    var _a = props.as, as = _a === void 0 ? 'div' : _a, rest = __rest(props, ["as"]);
    return (React__default.createElement(ForwardingBox, __assign({ ref: ref, as: RTabList, forwardedAs: as, bg: "inherit" }, rest)));
});
var RTabList = React__default.forwardRef(function (props, r) {
    var forwardedAs = props.forwardedAs, rest = __rest(props, ["forwardedAs"]);
    return React__default.createElement(TabList$1, __assign({ ref: r, as: forwardedAs }, rest));
});

var TabPanel = React__default.forwardRef(function (props, ref) {
    var _a = props.as, as = _a === void 0 ? 'div' : _a, rest = __rest(props, ["as"]);
    return (React__default.createElement(StyledTabPanel, __assign({ ref: ref, as: RTabPanel, forwardedAs: as }, rest)));
});
var StyledTabPanel = styled(ForwardingBox)({
    outline: 'none',
});
var RTabPanel = React__default.forwardRef(function (props, r) {
    var forwardedAs = props.forwardedAs, rest = __rest(props, ["forwardedAs"]);
    return React__default.createElement(TabPanel$1, __assign({ ref: r, as: forwardedAs }, rest));
});

var TabPanels = React__default.forwardRef(function (props, ref) {
    var _a = props.as, as = _a === void 0 ? 'div' : _a, _b = props.variant, variant = _b === void 0 ? 'default' : _b, rest = __rest(props, ["as", "variant"]);
    return (React__default.createElement(ForwardingBox, __assign({ ref: ref, as: RTabPanels, forwardedAs: as }, rest, { sx: {
            py: variant === 'underline' ? 'm' : 'l',
            borderTop: variant === 'underline'
                ? "1px solid ".concat(themeGet('colors.tab.underline.panel.border'))
                : 'none',
        } })));
});
var RTabPanels = React__default.forwardRef(function (props, r) {
    var forwardedAs = props.forwardedAs, rest = __rest(props, ["forwardedAs"]);
    return React__default.createElement(TabPanels$1, __assign({ ref: r, as: forwardedAs }, rest));
});

var SplitButton = function (props) {
    var dropDownItemsProps = props.dropDownItemsProps, dropDownListProps = props.dropDownListProps, dropDownButtonProps = props.dropDownButtonProps, rest = __rest(props, ["dropDownItemsProps", "dropDownListProps", "dropDownButtonProps"]);
    // This is needed, we'll fix it!
    var buttonProps = rest;
    if (!dropDownItemsProps || dropDownItemsProps.length === 0) {
        return React__default.createElement(Button, __assign({}, buttonProps));
    }
    var width = buttonProps.width, buttonPropsWithoutWidth = __rest(buttonProps, ["width"]);
    return (React__default.createElement(DropdownMenu, null,
        React__default.createElement(Flex, { width: width },
            React__default.createElement(Button
            // Come back for this!
            , __assign({}, buttonPropsWithoutWidth, { className: "split-button", borderRight: "solid 1px rgba(0, 0, 0, 0.2)", style: {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                }, flex: 1 })),
            React__default.createElement(DropdownMenuTrigger, __assign({ className: "split-dropdownbutton", variantColor: rest.variantColor, variant: rest.variant, size: rest.size, as: IconButton, icon: ChevronDown, disabled: rest.disabled, style: {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                }, flex: 0 }, dropDownButtonProps))),
        React__default.createElement(DropdownMenuList, __assign({}, dropDownListProps), dropDownItemsProps.map(function (itemProps, index) { return (React__default.createElement(DropdownMenuItem, __assign({ key: itemProps.id || index }, itemProps))); }))));
};

var Breadcrumb = forwardRef(function Breadcrumb(props, ref) {
    var space = props.space, separator = props.separator, children = props.children, rest = __rest(props, ["space", "separator", "children"]);
    var validChildren = Children.toArray(children).filter(isValidElement);
    var count = validChildren.length;
    var clones = validChildren.map(function (child, index) {
        return cloneElement(child, {
            separator: separator,
            space: space,
            isLastChild: count === index + 1,
        });
    });
    return (createElement(Box, __assign({ ref: ref, as: "nav", "aria-label": "breadcrumb" }, rest),
        createElement(Box, { as: "ol" }, clones)));
});
var BreadcrumbItem = forwardRef(function BreadcrumbItem(props, ref) {
    var separator = props.separator, _a = props.space, space = _a === void 0 ? 's' : _a, isLastChild = props.isLastChild, isCurrentPage = props.isCurrentPage, disabled = props.disabled, children = props.children, rest = __rest(props, ["separator", "space", "isLastChild", "isCurrentPage", "disabled", "children"]);
    var validChildren = Children.toArray(children).filter(isValidElement);
    var clones = validChildren.map(function (child, index) {
        return cloneElement(child, {
            isCurrentPage: isCurrentPage,
            disabled: disabled,
        });
    });
    return (createElement(Box, __assign({ ref: ref, as: "li", color: isCurrentPage ? 'breadcrumb.link.current' : 'breadcrumb.link.default', display: "inline-flex", alignItems: "center", verticalAlign: "middle", m: 0, p: 0 }, rest),
        clones,
        !isLastChild && (createElement(Box, { as: "span", color: "breadcrumb.separator", role: "presentation", mx: space, sx: separator
                ? {}
                : {
                    display: 'inline-block',
                    height: '18px',
                    transform: 'rotate(15deg)',
                    width: '1px',
                    backgroundColor: themeGet("colors.breadcrumb.separator"),
                } }, separator))));
});
var BreadcrumbLink = forwardRef(function BreadcrumbLink(props, ref) {
    var isCurrentPage = props.isCurrentPage, disabled = props.disabled, _a = props.as, as = _a === void 0 ? isCurrentPage || disabled ? 'span' : 'a' : _a, iconBefore = props.iconBefore, iconAfter = props.iconAfter, children = props.children, _b = props.sx, sx = _b === void 0 ? {} : _b, rest = __rest(props, ["isCurrentPage", "disabled", "as", "iconBefore", "iconAfter", "children", "sx"]);
    return (createElement(StackOld, __assign({ ref: ref, isInline: true, position: "relative", space: "xs", noWrap: true, alignItems: "center", width: "100%", justifyContent: "center", as: as, textDecoration: "none", color: isCurrentPage ? 'breadcrumb.link.current' : 'breadcrumb.link.default', fontSize: "copy", fontWeight: "medium", lineHeight: "sm", p: 0, border: "none", backgroundColor: "transparent", cursor: "pointer", sx: disabled
            ? sx
            : __assign({ outline: '0', '&:hover, &:focus, &[aria-expanded="true"]': {
                    color: 'breadcrumb.link.hover',
                }, '&:focus:before, &[aria-expanded="true"]:before': {
                    content: '""',
                    position: 'absolute',
                    top: '0',
                    right: '-1px',
                    bottom: '0',
                    left: '-1px',
                    borderRadius: '2px',
                    boxShadow: "0 0 0 2px ".concat(themeGet("colors.breadcrumb.focus_outline")),
                } }, sx) }, rest),
        iconBefore && (createElement(Box, { size: "icon.16", as: iconBefore, className: "baukasten-breadcrumb-link-icon-before" })),
        createElement(Flex, { as: "span", className: "baukasten-breadcrumb-link-inner" }, children),
        iconAfter && (createElement(Box, { size: "icon.16", as: iconAfter, className: "baukasten-breadcrumb-link-icon-after" }))));
});

var colors = baukastenTheme.colors;
var NavContext = createContext({
    isCollapsed: false,
});
var useNavContext = function () { return useContext(NavContext); };
var Nav = forwardRef(function Nav(_a, ref) {
    var _b = _a.isCollapsed, isCollapsed = _b === void 0 ? false : _b, _c = _a.collapsedWidth, collapsedWidth = _c === void 0 ? '64' : _c, _d = _a.width, width = _d === void 0 ? '208' : _d, onCollapse = _a.onCollapse, onExpand = _a.onExpand, children = _a.children, sx = _a.sx, rest = __rest(_a, ["isCollapsed", "collapsedWidth", "width", "onCollapse", "onExpand", "children", "sx"]);
    useEffect(function () {
        if (isCollapsed) {
            onCollapse === null || onCollapse === void 0 ? void 0 : onCollapse();
        }
        else {
            onExpand === null || onExpand === void 0 ? void 0 : onExpand();
        }
    }, [isCollapsed]);
    return (React__default.createElement(Box, __assign({ bg: colors.nav.background, minHeight: "100vh", height: "100vh", py: "16", px: "12", position: "relative", maxWidth: isCollapsed ? collapsedWidth : width, minWidth: isCollapsed ? collapsedWidth : width, width: isCollapsed ? collapsedWidth : width, ref: ref, sx: mergeSx({
            '&:hover': {
                '.nav-collpase-trigger': {
                    display: 'inline-flex',
                },
            },
            transition: 'all 0.2s',
        }, sx) }, rest),
        React__default.createElement(NavContext.Provider, { value: { isCollapsed: isCollapsed } }, children)));
});

var colors$1 = baukastenTheme.colors;
var NavItem = React__default.forwardRef(function NavItem(_a, ref) {
    var childBefore = _a.childBefore, icon = _a.icon, _b = _a.tooltipLabel, tooltipLabel = _b === void 0 ? '' : _b, childBeforeContainerProps = _a.childBeforeContainerProps, children = _a.children, sx = _a.sx, rest = __rest(_a, ["childBefore", "icon", "tooltipLabel", "childBeforeContainerProps", "children", "sx"]);
    var isCollapsed = useNavContext().isCollapsed;
    return (React__default.createElement(Inline, __assign({ alignItems: "center", tabIndex: 0, width: "100%", gap: "8", as: "a", borderRadius: "small", cursor: "pointer", sx: mergeSx({
            color: "".concat(colors$1.nav.item.inactive.default.foreground),
            fontWeight: 'medium',
            outline: 'none',
            '&:hover, &:focus, &:focus-within': {
                bg: colors$1.nav.item.inactive.hover.background,
            },
            '&:hover, &:focus, &:focus-within, &:active, &.active, &[data-selected]': {
                outline: 'none',
            },
            '&:focus, &:focus-within': {
                boxShadow: "0 0 0 2px ".concat(colors$1.nav.item.inactive.focus_outline),
            },
            '&.active, &[data-selected]': {
                color: colors$1.nav.item.active.default.foreground,
                bg: colors$1.nav.item.active.default.background,
                '&:focus, &:focus-within': {
                    boxShadow: "0 0 0 2px ".concat(colors$1.nav.item.active.focus_outline),
                },
            },
        }, sx), ref: ref }, rest),
        React__default.createElement(Tooltip, { placement: "right", label: isCollapsed ? tooltipLabel : '' },
            React__default.createElement(Flex, __assign({ alignItems: "center", justifyContent: "center", width: "40", height: "36", py: icon ? '4' : '0', px: icon ? '8' : '4' }, (childBeforeContainerProps || {})), icon ? (React__default.createElement(Box, { as: icon })) : childBefore ? (React__default.createElement(Box, { as: childBefore })) : null)),
        !isCollapsed && children));
});

var NavHeader = forwardRef(function NavHeader(props, ref) {
    var isCollapsed = useNavContext().isCollapsed;
    var children = props.children, expand = props.expand, collapse = props.collapse, sx = props.sx, _a = props.width, width = _a === void 0 ? '184' : _a, _b = props.logoContainerProps, logoContainerProps = _b === void 0 ? { sx: {} } : _b, rest = __rest(props, ["children", "expand", "collapse", "sx", "width", "logoContainerProps"]);
    var logoSX = logoContainerProps.sx, restOfLogoProps = __rest(logoContainerProps, ["sx"]);
    if (isCollapsed)
        return (React__default.createElement(Inline, __assign({ ref: ref, sx: mergeSx({
                '&:hover': {
                    '.nav-expand-trigger': {
                        display: 'inline-flex !important',
                    },
                    '.menu-icon': {
                        display: 'none !important',
                    },
                },
                '.menu-icon': {
                    ':focus': {
                        boxShadow: 'none !important',
                        background: 'inherit !important',
                    },
                },
            }, sx), height: "40" }, rest),
            React__default.createElement(IconButton, { display: "none", className: "nav-expand-trigger", variantColor: "secondary", onClick: expand, variant: "ghost", iconSize: "icon.24", size: "large", icon: DoubleChevronRight }),
            React__default.createElement(IconButton, { className: "menu-icon", variantColor: "secondary", size: "large", iconSize: "icon.24", variant: "ghost", icon: Menu$1 })));
    return (React__default.createElement(Inline, __assign({ ref: ref, width: width, minWidth: width, overflow: "hidden", height: "40", gap: "4", alignItems: "center" }, rest),
        React__default.createElement(Flex, __assign({ width: "156", sx: mergeSx({
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }, logoSX) }, (restOfLogoProps || {})), children),
        React__default.createElement(IconButton, { display: "none", className: "nav-collpase-trigger", variantColor: "secondary", size: "small", iconSize: "icon.24", onClick: collapse, variant: "ghost", icon: DoubleChevronLeft })));
});

function Select(props) {
    var label = props.label, children = props.children, triggerIcon = props.triggerIcon, _a = props.placeholder, Component = props.as, _b = props.size, size = _b === void 0 ? 'medium' : _b, maxWidth = props.maxWidth, defaultValue = props.defaultValue, onValueChange = props.onValueChange, sx = props.sx, rest = __rest(props, ["label", "children", "triggerIcon", "placeholder", "as", "size", "maxWidth", "defaultValue", "onValueChange", "sx"]);
    var _c = __read(React__default.useState(defaultValue), 2), value = _c[0], setValue = _c[1];
    var _d = __read(React__default.useState(0), 2), triggerWidth = _d[0], setTriggerWidth = _d[1];
    var _e = __read(React__default.useState(0), 2), triggerLeftPosition = _e[0], setTriggerLeftPosition = _e[1];
    var triggerRef = useCallback(function (node) {
        if (node !== null) {
            setTriggerWidth(node.getBoundingClientRect().width);
            setTriggerLeftPosition(node.getBoundingClientRect().left);
        }
    }, []);
    var handleValueChange = function (value) {
        setValue(value);
        onValueChange === null || onValueChange === void 0 ? void 0 : onValueChange(value);
    };
    var triggerProps = Component
        ? rest
        : {
            variant: 'outline',
            iconBefore: triggerIcon,
            iconAfter: CaretDownFill,
            className: 'selectTrigger',
            fontWeight: 500,
            ref: triggerRef,
            size: size,
        };
    var TriggerComponent = Component !== null && Component !== void 0 ? Component : Button;
    return (React__default.createElement(Root$1, { value: value, defaultValue: defaultValue, onValueChange: handleValueChange },
        React__default.createElement(Trigger$1, { asChild: true },
            React__default.createElement(TriggerComponent, __assign({}, triggerProps),
                props.label && React__default.createElement(Box, { as: "span" },
                    props.label,
                    ":"),
                React__default.createElement(Box, { as: "span", color: "select.trigger.foreground" },
                    "\u00A0",
                    React__default.createElement(Value, { placeholder: props.placeholder })))),
        React__default.createElement(Portal, null,
            React__default.createElement(Content$1, { asChild: true },
                React__default.createElement(Box, __assign({ backgroundColor: "dropdown.list.background", border: "1px solid", borderColor: "dropdown.list.border", borderRadius: "4", boxShadow: "dropdownMenuList", minWidth: triggerWidth > 60 ? triggerWidth : 60, maxWidth: maxWidth }, (props.label && {
                    position: 'fixed',
                    left: triggerLeftPosition,
                }), { sx: sx }),
                    React__default.createElement(ScrollUpButton, { asChild: true },
                        React__default.createElement(Inline, { alignItems: "center", justifyContent: "center" },
                            React__default.createElement(Box, { as: ChevronUp, color: "dropdown.item.selected.icon", size: "icon.16" }))),
                    React__default.createElement(Viewport, { asChild: true },
                        React__default.createElement(Stack, { p: "4", gap: "none" }, props.children)),
                    React__default.createElement(ScrollDownButton, { asChild: true },
                        React__default.createElement(Inline, { alignItems: "center", justifyContent: "center" },
                            React__default.createElement(Box, { as: ChevronDown, color: "dropdown.item.selected.icon", size: "icon.16" }))))))));
}
function Option(props) {
    var value = props.value, _a = props.as, Component = _a === void 0 ? Box : _a, children = props.children, description = props.description, iconBefore = props.iconBefore, disabled = props.disabled, rest = __rest(props, ["value", "as", "children", "description", "iconBefore", "disabled"]);
    return (React__default.createElement(Item$3, { asChild: true, value: props.value, disabled: disabled },
        React__default.createElement(Inline, { py: "8", pl: "32", pr: "36", borderRadius: "2", lineHeight: "small", cursor: "pointer", userSelect: "none", sx: {
                all: 'unset',
                position: 'relative',
                gap: '8',
                // Default styles
                '&[data-highlighted]': {
                    bg: 'dropdown.item.hover.background',
                },
                '&[data-highlighted]:focus:not(:hover)': {
                    outline: '2px solid',
                    outlineColor: 'dropdown.item.focus_outline',
                    zIndex: '2',
                },
                '&[data-highlighted]:hover': {
                    outline: '0',
                },
                '&[data-state="checked"]': {
                    gap: 0,
                    bg: 'dropdown.item.selected.background',
                    color: 'dropdown.item.selected.foreground',
                    outline: '0',
                },
                '&[data-state="checked"][data-highlighted]:hover, &[data-state="checked"][data-highlighted]:focus': {
                    bg: 'dropdown.item.selected.hover',
                },
                '&[data-state="checked"][data-highlighted]:focus': {
                    outlineColor: 'dropdown.item.selected.focus_outline',
                },
                '&[data-disabled]': {
                    userSelect: 'none',
                    color: 'dropdown.item.disabled',
                },
                '&[data-disabled]:hover': {
                    cursor: 'not-allowed',
                },
            } },
            !disabled && (React__default.createElement(ItemIndicator, null,
                React__default.createElement(Box, { position: "absolute", left: "8px", top: "8px", as: Check, size: "icon.16", color: "dropdown.item.selected.icon" }))),
            React__default.createElement(Component, __assign({}, rest),
                React__default.createElement(Inline, { gap: "8", flexWrap: "nowrap" },
                    iconBefore && (React__default.createElement(Box, { as: iconBefore, size: "icon.16", sx: {
                            color: 'dropdown.item.icon',
                            '[data-state="checked"] &': {
                                color: 'dropdown.item.selected.icon',
                            },
                            '[data-disabled] &': {
                                color: 'dropdown.item.disabled',
                            },
                        } })),
                    React__default.createElement(Stack, { gap: "4", fontWeight: description ? '500' : '400' },
                        React__default.createElement(ItemText, null, children),
                        description && (React__default.createElement(Box, { fontWeight: "400", sx: {
                                color: 'dropdown.item.description',
                                '[data-state="checked"] &': {
                                    color: 'dropdown.item.selected.description',
                                },
                            } }, description))))))));
}

var DEFAULT_THROTTLE = 16;
var useSizeObserver = function (throttle, throttleOptions) {
    if (throttle === void 0) { throttle = DEFAULT_THROTTLE; }
    var _a = __read(useState({}), 2), size = _a[0], setSize = _a[1];
    var onResize = useMemo(function () { return _.throttle(setSize, throttle, throttleOptions); }, [throttle]);
    var ref = useResizeObserver({ onResize: onResize }).ref;
    return __assign({ ref: ref }, size);
};
var useWidthObserver = function (throttle, throttleOptions) {
    if (throttle === void 0) { throttle = DEFAULT_THROTTLE; }
    var _a = __read(useState(), 2), width = _a[0], setWidth = _a[1];
    var onResize = useMemo(function () {
        return _.throttle(function (_a) {
            var width = _a.width;
            setWidth(width);
        }, throttle, throttleOptions);
    }, [throttle]);
    var ref = useResizeObserver({ onResize: onResize }).ref;
    return { ref: ref, width: width };
};
var useHeightObserver = function (throttle, throttleOptions) {
    if (throttle === void 0) { throttle = DEFAULT_THROTTLE; }
    var _a = __read(useState(), 2), height = _a[0], setHeight = _a[1];
    var onResize = useMemo(function () {
        return _.throttle(function (_a) {
            var height = _a.height;
            setHeight(height);
        }, throttle, throttleOptions);
    }, [throttle]);
    var ref = useResizeObserver({ onResize: onResize }).ref;
    return { ref: ref, height: height };
};

export { Alert, AlertLink, Avatar, Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Button, Card, CardContent, CardHeader, CheckBox, Collapse, Combobox, Dialog, DialogContent, DialogFooter, DialogHeader, Divider, DropdownMenu, DropdownMenuItem, DropdownMenuList, DropdownMenuTrigger, FieldSet, Flex, ForwardingBox, Grid, Heading, Hint, IconButton, Image, Inline, Input, InputAddonAfter, InputAddonBefore, InputError, InputGroup, InputGroupElementNames, InputInsetAfter, InputInsetBefore, Label, Menu, MenuItem, MultiSelect, Nav, NavHeader, NavItem, NumberInput, Option, PasswordInput, Pill, Progress, Radio, SearchInput, Select, Spinner, SplitButton, Stack, StackOld, Switch, Tab, TabList, TabPanel, TabPanels, Tabs, Text, TextArea, Tiles, Toast, ToastBox, ToastContainer, Tooltip, VisuallyHidden, closeToast, isToastActive, mergeSx, nativeHTMLPropAlias, splitDOMProps, themeGet, useHeightObserver, useNavContext, useSizeObserver, useToast, useWidthObserver };
//# sourceMappingURL=index.esm.js.map
